import { useState, useEffect } from 'react';
import { settingsApi, privacyApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Save, Settings, FileText, Eye, Clock, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Wrench, Calculator, Hammer, Sigma, Tag, Type, Palette, ChevronUp, ChevronDown, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';

const COLOR_OPTIONS = [
  { value: 'emerald', label: 'Grün', bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  { value: 'blue', label: 'Blau', bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  { value: 'amber', label: 'Gelb/Orange', bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  { value: 'red', label: 'Rot', bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
  { value: 'violet', label: 'Violett', bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300' },
  { value: 'slate', label: 'Grau', bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-300' },
];

const getColorClasses = (colorName) => {
  const color = COLOR_OPTIONS.find(c => c.value === colorName) || COLOR_OPTIONS[1];
  return color;
};

const REFURBISHMENT_LABELS = {
  roof: { label: 'Dachsanierung', desc: 'Typische Lebensdauer eines Daches' },
  heating: { label: 'Heizungsanlage', desc: 'Typische Lebensdauer einer Heizung' },
  windows: { label: 'Fenster', desc: 'Typische Lebensdauer von Fenstern' },
  facade_paint: { label: 'Fassadenanstrich', desc: 'Typische Lebensdauer eines Fassadenanstrichs' },
  electrical: { label: 'Elektroinstallation', desc: 'Typische Lebensdauer der Elektrik' },
  water_pipes: { label: 'Wasserleitungen', desc: 'Typische Lebensdauer von Wasserleitungen' },
  elevator_modernization: { label: 'Aufzugsmodernisierung', desc: 'Typische Modernisierungszyklen für Aufzüge' },
};

const DEFAULT_THRESHOLDS = {
  roof: 60,
  heating: 25,
  windows: 35,
  facade_paint: 20,
  electrical: 40,
  water_pipes: 50,
  elevator_modernization: 25,
};

const DEFAULT_THRESHOLDS_ARRAY = [
  { key: 'roof', label: 'Dachsanierung', years: 60, age_key: 'roof_age', renovation_key: 'roof', required_equipment: null, active: true },
  { key: 'heating', label: 'Heizungsanlage', years: 25, age_key: 'heating_age', renovation_key: 'heating', required_equipment: 'has_central_heating', active: true },
  { key: 'windows', label: 'Fenster', years: 35, age_key: 'windows_age', renovation_key: 'windows', required_equipment: null, active: true },
  { key: 'facade_paint', label: 'Fassadenanstrich', years: 20, age_key: 'facade_age', renovation_key: 'facade_paint', required_equipment: null, active: true },
  { key: 'electrical', label: 'Elektroinstallation', years: 40, age_key: 'electrical_age', renovation_key: 'electrical', required_equipment: null, active: true },
  { key: 'water_pipes', label: 'Wasserleitungen', years: 50, age_key: 'water_pipes_age', renovation_key: 'water_pipes', required_equipment: null, active: true },
  { key: 'elevator_modernization', label: 'Aufzugsmodernisierung', years: 25, age_key: 'elevator_age', renovation_key: 'elevator_modernization', required_equipment: 'has_elevator', active: true },
];

const EQUIPMENT_OPTIONS = [
  { value: '', label: 'Immer anzeigen' },
  { value: 'has_elevator', label: 'Nur mit Aufzug' },
  { value: 'has_central_heating', label: 'Nur mit Zentralheizung' },
  { value: 'has_underground_garage', label: 'Nur mit Tiefgarage' },
  { value: 'has_double_parkers', label: 'Nur mit Doppelparkern' },
];

const UNIT_OPTIONS = [
  { value: 'je Einheit', label: 'je Einheit' },
  { value: 'je Vorgang', label: 'je Vorgang' },
  { value: 'je Stunde', label: 'je Stunde' },
  { value: 'pauschal', label: 'pauschal' },
  { value: 'je Eigentümer', label: 'je Eigentümer' },
  { value: 'je Versammlung', label: 'je Versammlung' },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [privacy, setPrivacy] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Service dialog state
  const [serviceDialog, setServiceDialog] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name: '', price: 0, unit: 'je Vorgang', active: true });
  
  // Repair item dialog state
  const [repairDialog, setRepairDialog] = useState(false);
  const [editingRepairIndex, setEditingRepairIndex] = useState(null);
  const [repairForm, setRepairForm] = useState({ key: '', label: '', weight: 3, active: true });

  // Equipment item dialog state
  const [equipmentDialog, setEquipmentDialog] = useState(false);
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({ key: '', label: '', weight: 0, description: '', is_checkbox: true, active: true });

  // Building complexity item dialog state
  const [buildingDialog, setBuildingDialog] = useState(false);
  const [editingBuildingIndex, setEditingBuildingIndex] = useState(null);
  const [buildingForm, setBuildingForm] = useState({ key: '', label: '', type: 'per_building', points: 0, max_points: 15, threshold: 10, active: true, description: '' });

  // Rating label dialog state
  const [ratingDialog, setRatingDialog] = useState(false);
  const [editingRatingIndex, setEditingRatingIndex] = useState(null);
  const [ratingForm, setRatingForm] = useState({ key: '', label: '', color: 'blue', min_score: 0, max_score: 20, demand_pct: 100, active: true });

  // Generic move helper for reordering list items
  const moveItem = (listKey, fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    const list = [...(settings[listKey] || [])];
    if (toIndex < 0 || toIndex >= list.length) return;
    [list[fromIndex], list[toIndex]] = [list[toIndex], list[fromIndex]];
    setSettings({ ...settings, [listKey]: list });
  };

  // Move equipment item (uses getEquipmentItems helper)
  const moveEquipmentItem = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    const items = [...getEquipmentItems()];
    if (toIndex < 0 || toIndex >= items.length) return;
    [items[fromIndex], items[toIndex]] = [items[toIndex], items[fromIndex]];
    const weights = syncEquipmentWeights(items);
    setSettings({ ...settings, equipment_items: items, equipment_weights: weights });
  };

  useEffect(() => {
    Promise.all([settingsApi.get(), privacyApi.get()])
      .then(([sRes, pRes]) => {
        setSettings(sRes.data);
        setPrivacy(pRes.data.content || '');
      })
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await settingsApi.update({
        base_fee_per_unit: settings.base_fee_per_unit,
        base_fee_per_unit_active: settings.base_fee_per_unit_active,
        hourly_rate: settings.hourly_rate,
        minimum_fee: settings.minimum_fee,
        minimum_fee_reduction_percent: settings.minimum_fee_reduction_percent,
        fee_tiers: settings.fee_tiers,
        fee_tiers_config: settings.fee_tiers_config,
        additional_services: settings.additional_services,
        public_visibility: settings.public_visibility,
        refurbishment_thresholds: settings.refurbishment_thresholds,
        refurbishment_thresholds_list: settings.refurbishment_thresholds_list,
        renovation_fee_percent: settings.renovation_fee_percent,
        repair_items: settings.repair_items,
        modernization_weights: settings.modernization_weights,
        equipment_weights: settings.equipment_weights,
        equipment_items: settings.equipment_items,
        building_complexity_items: settings.building_complexity_items,
        building_condition_params: settings.building_condition_params,
        refurbishment_cost_estimates: settings.refurbishment_cost_estimates,
        modernization_items: settings.modernization_items,
        fee_calculation_params: settings.fee_calculation_params,
        history_items: settings.history_items,
        assessment_labels: settings.assessment_labels,
        rating_labels: settings.rating_labels,
        recommendation_tiers: settings.recommendation_tiers,
        score_weights: settings.score_weights,
        homepage_link_text: settings.homepage_link_text,
        seo_section: settings.seo_section,
        meta_tags: settings.meta_tags,
      });
      toast.success('Einstellungen gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    setSaving(false);
  };

  const savePrivacy = async () => {
    setSaving(true);
    try {
      await privacyApi.update({ content: privacy });
      toast.success('Datenschutzseite gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    setSaving(false);
  };

  const toggleVisibility = (key) => {
    setSettings({
      ...settings,
      public_visibility: {
        ...settings.public_visibility,
        [key]: !settings.public_visibility[key],
      },
    });
  };

  // Threshold dialog state
  const [thresholdDialog, setThresholdDialog] = useState(false);
  const [editingThresholdIndex, setEditingThresholdIndex] = useState(null);
  const [thresholdForm, setThresholdForm] = useState({ key: '', label: '', years: 30, age_key: '', renovation_key: '', required_equipment: '', active: true });

  // Helper to get thresholds array (migrate from old dict format)
  const getThresholdsArray = () => {
    if (Array.isArray(settings.refurbishment_thresholds_list)) {
      return settings.refurbishment_thresholds_list;
    }
    // Migrate from old dict format
    const dict = settings.refurbishment_thresholds || DEFAULT_THRESHOLDS;
    return DEFAULT_THRESHOLDS_ARRAY.map(t => ({
      ...t,
      years: dict[t.key] ?? t.years,
    }));
  };

  const updateThreshold = (key, value) => {
    const thresholds = settings.refurbishment_thresholds || DEFAULT_THRESHOLDS;
    setSettings({
      ...settings,
      refurbishment_thresholds: {
        ...thresholds,
        [key]: parseInt(value) || 0,
      },
    });
  };

  const resetThresholds = () => {
    setSettings({
      ...settings,
      refurbishment_thresholds: { ...DEFAULT_THRESHOLDS },
      refurbishment_thresholds_list: [...DEFAULT_THRESHOLDS_ARRAY],
    });
    toast.info('Schwellenwerte auf Standard zurückgesetzt');
  };

  // Threshold CRUD functions
  const openAddThresholdDialog = () => {
    setEditingThresholdIndex(null);
    setThresholdForm({ key: '', label: '', years: 30, age_key: '', renovation_key: '', required_equipment: '', active: true });
    setThresholdDialog(true);
  };

  const openEditThresholdDialog = (index) => {
    const item = getThresholdsArray()[index];
    setEditingThresholdIndex(index);
    setThresholdForm({ ...item, required_equipment: item.required_equipment || '' });
    setThresholdDialog(true);
  };

  const saveThresholdItem = () => {
    const items = [...getThresholdsArray()];
    const formData = { ...thresholdForm, required_equipment: thresholdForm.required_equipment || null };
    if (!formData.key && formData.label) {
      formData.key = formData.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
    if (!formData.age_key && formData.key) {
      formData.age_key = formData.key + '_age';
    }
    if (!formData.renovation_key && formData.key) {
      formData.renovation_key = formData.key;
    }
    if (editingThresholdIndex !== null) {
      items[editingThresholdIndex] = formData;
    } else {
      items.push(formData);
    }
    // Also update the old dict format for backward compatibility
    const dictThresholds = {};
    items.forEach(t => { dictThresholds[t.key] = t.years; });
    setSettings({ ...settings, refurbishment_thresholds_list: items, refurbishment_thresholds: dictThresholds });
    setThresholdDialog(false);
    toast.success(editingThresholdIndex !== null ? 'Schwellenwert aktualisiert' : 'Schwellenwert hinzugefügt');
  };

  const toggleThresholdActive = (index) => {
    const items = [...getThresholdsArray()];
    items[index] = { ...items[index], active: !items[index].active };
    const dictThresholds = {};
    items.filter(t => t.active !== false).forEach(t => { dictThresholds[t.key] = t.years; });
    setSettings({ ...settings, refurbishment_thresholds_list: items, refurbishment_thresholds: dictThresholds });
  };

  const deleteThresholdItem = (index) => {
    const items = getThresholdsArray().filter((_, i) => i !== index);
    const dictThresholds = {};
    items.filter(t => t.active !== false).forEach(t => { dictThresholds[t.key] = t.years; });
    setSettings({ ...settings, refurbishment_thresholds_list: items, refurbishment_thresholds: dictThresholds });
    toast.success('Schwellenwert gelöscht');
  };

  const updateThresholdYears = (index, years) => {
    const items = [...getThresholdsArray()];
    items[index] = { ...items[index], years: parseInt(years) || 0 };
    const dictThresholds = {};
    items.filter(t => t.active !== false).forEach(t => { dictThresholds[t.key] = t.years; });
    setSettings({ ...settings, refurbishment_thresholds_list: items, refurbishment_thresholds: dictThresholds });
  };

  // Service CRUD functions
  const openAddServiceDialog = () => {
    setEditingServiceIndex(null);
    setServiceForm({ name: '', price: 0, unit: 'je Vorgang', active: true });
    setServiceDialog(true);
  };

  const openEditServiceDialog = (index) => {
    const service = settings.additional_services[index];
    setEditingServiceIndex(index);
    setServiceForm({ ...service });
    setServiceDialog(true);
  };

  const saveService = () => {
    const services = [...(settings.additional_services || [])];
    if (editingServiceIndex !== null) {
      services[editingServiceIndex] = { ...serviceForm };
    } else {
      services.push({ ...serviceForm });
    }
    setSettings({ ...settings, additional_services: services });
    setServiceDialog(false);
    toast.success(editingServiceIndex !== null ? 'Leistung aktualisiert' : 'Leistung hinzugefügt');
  };

  const toggleServiceActive = (index) => {
    const services = [...(settings.additional_services || [])];
    services[index] = { ...services[index], active: !services[index].active };
    setSettings({ ...settings, additional_services: services });
  };

  const deleteService = (index) => {
    const services = settings.additional_services.filter((_, i) => i !== index);
    setSettings({ ...settings, additional_services: services });
    toast.success('Leistung gelöscht');
  };

  const updateService = (index, field, value) => {
    const services = [...(settings.additional_services || [])];
    services[index] = { ...services[index], [field]: value };
    setSettings({ ...settings, additional_services: services });
  };

  // Repair item CRUD functions
  const openAddRepairDialog = () => {
    setEditingRepairIndex(null);
    setRepairForm({ key: '', label: '', weight: 3, active: true });
    setRepairDialog(true);
  };

  const openEditRepairDialog = (index) => {
    const item = settings.repair_items[index];
    setEditingRepairIndex(index);
    setRepairForm({ ...item, required_equipment: item.required_equipment || '' });
    setRepairDialog(true);
  };

  const saveRepairItem = () => {
    const items = [...(settings.repair_items || [])];
    const formData = { ...repairForm, required_equipment: repairForm.required_equipment || null };
    if (!formData.key && formData.label) {
      formData.key = formData.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
    if (editingRepairIndex !== null) {
      items[editingRepairIndex] = formData;
    } else {
      items.push(formData);
    }
    setSettings({ ...settings, repair_items: items });
    setRepairDialog(false);
    toast.success(editingRepairIndex !== null ? 'Reparaturtyp aktualisiert' : 'Reparaturtyp hinzugefügt');
  };

  const toggleRepairActive = (index) => {
    const items = [...(settings.repair_items || [])];
    items[index] = { ...items[index], active: !items[index].active };
    setSettings({ ...settings, repair_items: items });
  };

  const deleteRepairItem = (index) => {
    const items = settings.repair_items.filter((_, i) => i !== index);
    setSettings({ ...settings, repair_items: items });
    toast.success('Reparaturtyp gelöscht');
  };

  const updateRepairWeight = (index, weight) => {
    const items = [...(settings.repair_items || [])];
    items[index] = { ...items[index], weight: parseInt(weight) || 0 };
    setSettings({ ...settings, repair_items: items });
  };

  // Equipment items CRUD functions
  const DEFAULT_EQUIPMENT_ITEMS = [
    {key: "has_elevator", label: "Personenaufzug", weight: 12, description: "Wartung, Prüfungen, TÜV-Abnahmen erhöhen Verwaltungsaufwand", is_checkbox: true, active: true},
    {key: "has_underground_garage", label: "Tiefgarage", weight: 10, description: "Zusätzliche Technik und separate Abrechnung möglich", is_checkbox: true, active: true},
    {key: "has_double_parkers", label: "Doppelparker / Hebebühnen", weight: 18, description: "Hoher Wartungsaufwand, häufige Reparaturen", is_checkbox: true, active: true},
    {key: "has_sub_communities", label: "Untergemeinschaften (sep. Abrechnung)", weight: 20, description: "Separate Abrechnungskreise erhöhen Buchungsaufwand erheblich", is_checkbox: true, active: true},
    {key: "no_central_heating", label: "Keine zentrale Heizungsanlage", weight: -8, description: "Negativer Wert, da weniger zentrale Wartung erforderlich", is_checkbox: true, invert_field: "has_central_heating", active: true},
  ];

  const getEquipmentItems = () => {
    if (Array.isArray(settings?.equipment_items) && settings.equipment_items.length > 0) {
      return settings.equipment_items;
    }
    // Migrate from old equipment_weights dict
    const weights = settings?.equipment_weights || {};
    return DEFAULT_EQUIPMENT_ITEMS.map(item => ({
      ...item,
      weight: weights[item.key] ?? item.weight,
    }));
  };

  const syncEquipmentWeights = (items) => {
    const weights = {};
    items.filter(i => i.active !== false).forEach(i => { weights[i.key] = i.weight; });
    return weights;
  };

  const openAddEquipmentDialog = () => {
    setEditingEquipmentIndex(null);
    setEquipmentForm({ key: '', label: '', weight: 5, description: '', is_checkbox: true, active: true });
    setEquipmentDialog(true);
  };

  const openEditEquipmentDialog = (index) => {
    const item = getEquipmentItems()[index];
    setEditingEquipmentIndex(index);
    setEquipmentForm({ ...item });
    setEquipmentDialog(true);
  };

  const saveEquipmentItem = () => {
    const items = [...getEquipmentItems()];
    const formData = { ...equipmentForm };
    if (!formData.key && formData.label) {
      formData.key = 'custom_' + formData.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
    if (editingEquipmentIndex !== null) {
      items[editingEquipmentIndex] = formData;
    } else {
      items.push(formData);
    }
    const weights = syncEquipmentWeights(items);
    setSettings({ ...settings, equipment_items: items, equipment_weights: weights });
    setEquipmentDialog(false);
    toast.success(editingEquipmentIndex !== null ? 'Ausstattungsmerkmal aktualisiert' : 'Ausstattungsmerkmal hinzugefügt');
  };

  const toggleEquipmentActive = (index) => {
    const items = [...getEquipmentItems()];
    items[index] = { ...items[index], active: !items[index].active };
    const weights = syncEquipmentWeights(items);
    setSettings({ ...settings, equipment_items: items, equipment_weights: weights });
  };

  const deleteEquipmentItem = (index) => {
    const items = getEquipmentItems().filter((_, i) => i !== index);
    const weights = syncEquipmentWeights(items);
    setSettings({ ...settings, equipment_items: items, equipment_weights: weights });
    toast.success('Ausstattungsmerkmal gelöscht');
  };

  const updateEquipmentWeight = (index, weight) => {
    const items = [...getEquipmentItems()];
    items[index] = { ...items[index], weight: parseInt(weight) || 0 };
    const weights = syncEquipmentWeights(items);
    setSettings({ ...settings, equipment_items: items, equipment_weights: weights });
  };

  // Building complexity item CRUD
  const getBuildingItems = () => settings.building_complexity_items || [];

  const saveBuildingItem = () => {
    const items = [...getBuildingItems()];
    const formData = { ...buildingForm };
    if (!formData.key && formData.label) {
      formData.key = 'bld_' + formData.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
    if (editingBuildingIndex !== null) {
      items[editingBuildingIndex] = formData;
    } else {
      items.push(formData);
    }
    setSettings({ ...settings, building_complexity_items: items });
    setBuildingDialog(false);
    toast.success(editingBuildingIndex !== null ? 'Gebäuderegel aktualisiert' : 'Gebäuderegel hinzugefügt');
  };

  const deleteBuildingItem = (index) => {
    const items = getBuildingItems().filter((_, i) => i !== index);
    setSettings({ ...settings, building_complexity_items: items });
    toast.success('Gebäuderegel gelöscht');
  };

  const toggleBuildingActive = (index) => {
    const items = [...getBuildingItems()];
    items[index] = { ...items[index], active: !items[index].active };
    setSettings({ ...settings, building_complexity_items: items });
  };

  const moveBuildingItem = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    const items = [...getBuildingItems()];
    if (toIndex < 0 || toIndex >= items.length) return;
    [items[fromIndex], items[toIndex]] = [items[toIndex], items[fromIndex]];
    setSettings({ ...settings, building_complexity_items: items });
  };

  // Rating labels CRUD
  const DEFAULT_RATING_LABELS = [
    { key: 'sehr_gering', label: 'Sehr Gering', color: 'emerald', min_score: 0, max_score: 20, demand_pct: 50, active: true },
    { key: 'gering', label: 'Gering', color: 'blue', min_score: 21, max_score: 35, demand_pct: 75, active: true },
    { key: 'durchschnittlich', label: 'Durchschnittlich', color: 'slate', min_score: 36, max_score: 55, demand_pct: 100, active: true },
    { key: 'hoch', label: 'Hoch', color: 'amber', min_score: 56, max_score: 75, demand_pct: 150, active: true },
    { key: 'sehr_hoch', label: 'Sehr Hoch', color: 'red', min_score: 76, max_score: 100, demand_pct: 150, active: true },
  ];

  const getRatingLabels = () => settings.rating_labels || DEFAULT_RATING_LABELS;

  const openAddRatingDialog = () => {
    setEditingRatingIndex(null);
    // Find the next available score range
    const items = getRatingLabels();
    const maxScore = items.length > 0 ? Math.max(...items.map(i => i.max_score || 0)) + 1 : 0;
    setRatingForm({ key: '', label: '', color: 'blue', min_score: maxScore, max_score: maxScore + 20, demand_pct: 100, active: true });
    setRatingDialog(true);
  };

  const openEditRatingDialog = (index) => {
    const item = getRatingLabels()[index];
    setEditingRatingIndex(index);
    setRatingForm({ ...item });
    setRatingDialog(true);
  };

  const saveRatingItem = () => {
    const items = [...getRatingLabels()];
    const formData = { ...ratingForm };
    if (!formData.key && formData.label) {
      formData.key = formData.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
    if (editingRatingIndex !== null) {
      items[editingRatingIndex] = formData;
    } else {
      items.push(formData);
    }
    // Sort by min_score
    items.sort((a, b) => (a.min_score || 0) - (b.min_score || 0));
    setSettings({ ...settings, rating_labels: items });
    setRatingDialog(false);
    toast.success(editingRatingIndex !== null ? 'Bewertungslabel aktualisiert' : 'Bewertungslabel hinzugefügt');
  };

  const deleteRatingItem = (index) => {
    const items = getRatingLabels().filter((_, i) => i !== index);
    setSettings({ ...settings, rating_labels: items });
    toast.success('Bewertungslabel gelöscht');
  };

  const toggleRatingActive = (index) => {
    const items = [...getRatingLabels()];
    items[index] = { ...items[index], active: !items[index].active };
    setSettings({ ...settings, rating_labels: items });
  };

  const resetRatingLabels = () => {
    setSettings({ ...settings, rating_labels: [...DEFAULT_RATING_LABELS] });
    toast.info('Bewertungslabels auf Standard zurückgesetzt');
  };

  // Cost estimate functions
  const getCostEstimates = () => settings.refurbishment_cost_estimates || [];

  const updateCostEstimate = (index, field, value) => {
    const items = [...getCostEstimates()];
    items[index] = { ...items[index], [field]: value };
    setSettings({ ...settings, refurbishment_cost_estimates: items });
  };

  const updateCostBracket = (itemIndex, bracketIndex, field, value) => {
    const items = [...getCostEstimates()];
    const brackets = [...(items[itemIndex].brackets || [])];
    brackets[bracketIndex] = { ...brackets[bracketIndex], [field]: parseInt(value) || 0 };
    items[itemIndex] = { ...items[itemIndex], brackets };
    setSettings({ ...settings, refurbishment_cost_estimates: items });
  };

  const updateCostTier = (itemIndex, tierIndex, field, value) => {
    const items = [...getCostEstimates()];
    const tiers = [...(items[itemIndex].tiers || [])];
    tiers[tierIndex] = { ...tiers[tierIndex], [field]: parseInt(value) || 0 };
    items[itemIndex] = { ...items[itemIndex], tiers };
    setSettings({ ...settings, refurbishment_cost_estimates: items });
  };

  const addCostEstimate = () => {
    const items = [...getCostEstimates()];
    items.push({ key: '', label: '', type: 'per_unit', cost_per_unit: 0, active: true });
    setSettings({ ...settings, refurbishment_cost_estimates: items });
  };

  const deleteCostEstimate = (index) => {
    const items = getCostEstimates().filter((_, i) => i !== index);
    setSettings({ ...settings, refurbishment_cost_estimates: items });
    toast.success('Kostenschätzung gelöscht');
  };

  // Modernization item dialog state
  const [modernizationDialog, setModernizationDialog] = useState(false);
  const [editingModernizationIndex, setEditingModernizationIndex] = useState(null);
  const [modernizationForm, setModernizationForm] = useState({ key: '', label: '', required_equipment: '', active: true });

  // Modernization items CRUD functions
  const getModernizationItems = () => settings?.modernization_items || [];

  const openAddModernizationDialog = () => {
    setEditingModernizationIndex(null);
    setModernizationForm({ key: '', label: '', required_equipment: '', active: true });
    setModernizationDialog(true);
  };

  const openEditModernizationDialog = (index) => {
    const item = getModernizationItems()[index];
    setEditingModernizationIndex(index);
    setModernizationForm({ ...item, required_equipment: item.required_equipment || '' });
    setModernizationDialog(true);
  };

  const saveModernizationItem = () => {
    const items = [...getModernizationItems()];
    const formData = { ...modernizationForm, required_equipment: modernizationForm.required_equipment || null };
    if (!formData.key && formData.label) {
      formData.key = formData.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
    if (editingModernizationIndex !== null) {
      items[editingModernizationIndex] = formData;
    } else {
      items.push(formData);
    }
    setSettings({ ...settings, modernization_items: items });
    setModernizationDialog(false);
    toast.success(editingModernizationIndex !== null ? 'Modernisierungsposten aktualisiert' : 'Modernisierungsposten hinzugefügt');
  };

  const toggleModernizationActive = (index) => {
    const items = [...getModernizationItems()];
    items[index] = { ...items[index], active: !items[index].active };
    setSettings({ ...settings, modernization_items: items });
  };

  const deleteModernizationItem = (index) => {
    const items = getModernizationItems().filter((_, i) => i !== index);
    setSettings({ ...settings, modernization_items: items });
    toast.success('Modernisierungsposten gelöscht');
  };

  // History item dialog state
  const [historyDialog, setHistoryDialog] = useState(false);
  const [editingHistoryIndex, setEditingHistoryIndex] = useState(null);
  const [historyForm, setHistoryForm] = useState({ 
    key: '', label: '', type: 'freq', 
    weight_none: 0, weight_selten: 8, weight_haeufig: 18,
    weight_niedrig: 0, weight_mittel: 8, weight_hoch: 15,
    weight_alle: 0, weight_meiste: 8, weight_wenige: 18,
    weight_true: 0, weight_false: 12,
    active: true 
  });

  // History items CRUD functions
  const getHistoryItems = () => settings?.history_items || [];

  const openAddHistoryDialog = () => {
    setEditingHistoryIndex(null);
    setHistoryForm({ 
      key: '', label: '', type: 'freq', 
      weight_none: 0, weight_selten: 8, weight_haeufig: 18,
      weight_niedrig: 0, weight_mittel: 8, weight_hoch: 15,
      weight_alle: 0, weight_meiste: 8, weight_wenige: 18,
      weight_true: 0, weight_false: 12,
      active: true 
    });
    setHistoryDialog(true);
  };

  const openEditHistoryDialog = (index) => {
    const item = getHistoryItems()[index];
    setEditingHistoryIndex(index);
    setHistoryForm({ ...item });
    setHistoryDialog(true);
  };

  const saveHistoryItem = () => {
    const items = [...getHistoryItems()];
    const formData = { ...historyForm };
    if (!formData.key && formData.label) {
      formData.key = formData.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
    if (editingHistoryIndex !== null) {
      items[editingHistoryIndex] = formData;
    } else {
      items.push(formData);
    }
    setSettings({ ...settings, history_items: items });
    setHistoryDialog(false);
    toast.success(editingHistoryIndex !== null ? 'Historie-Punkt aktualisiert' : 'Historie-Punkt hinzugefügt');
  };

  const toggleHistoryActive = (index) => {
    const items = [...getHistoryItems()];
    items[index] = { ...items[index], active: !items[index].active };
    setSettings({ ...settings, history_items: items });
  };

  const deleteHistoryItem = (index) => {
    const items = getHistoryItems().filter((_, i) => i !== index);
    setSettings({ ...settings, history_items: items });
    toast.success('Historie-Punkt gelöscht');
  };

  const HISTORY_TYPE_OPTIONS = [
    { value: 'freq', label: 'Häufigkeit (Keine/Selten/Häufig)' },
    { value: 'level', label: 'Niveau (Niedrig/Mittel/Hoch)' },
    { value: 'level_inv', label: 'Niveau invertiert (Hoch=Gut)' },
    { value: 'sepa', label: 'SEPA (Alle/Meiste/Wenige)' },
    { value: 'bool_inv', label: 'Ja/Nein invertiert (Ja=Gut)' },
  ];

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">Laden...</div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl" data-testid="admin-settings">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Einstellungen</h1>
        <p className="text-sm text-muted-foreground mt-1">Vergütung, Sichtbarkeit und Datenschutz verwalten</p>
      </div>

      <Tabs defaultValue="fees" className="space-y-6">
        <TabsList data-testid="settings-tabs" className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-11 w-full h-auto p-1 gap-1">
          <TabsTrigger value="fees" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-fees"><Settings className="h-3.5 w-3.5" /> Vergütung</TabsTrigger>
          <TabsTrigger value="equipment" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-equipment"><ToggleRight className="h-3.5 w-3.5" /> Ausstattung</TabsTrigger>
          <TabsTrigger value="condition" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-condition"><Building2 className="h-3.5 w-3.5" /> Geb.Zustand</TabsTrigger>
          <TabsTrigger value="formulas" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-formulas"><Sigma className="h-3.5 w-3.5" /> Berechnung</TabsTrigger>
          <TabsTrigger value="assessments" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-assessments"><Palette className="h-3.5 w-3.5" /> Bewertung</TabsTrigger>
          <TabsTrigger value="texts" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-texts"><Type className="h-3.5 w-3.5" /> Texte</TabsTrigger>
          <TabsTrigger value="meta" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-meta"><Globe className="h-3.5 w-3.5" /> Meta/SEO</TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-history"><Clock className="h-3.5 w-3.5" /> Historie</TabsTrigger>
          <TabsTrigger value="modernizations" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-modernizations"><Hammer className="h-3.5 w-3.5" /> Modern.</TabsTrigger>
          <TabsTrigger value="repairs" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-repairs"><Wrench className="h-3.5 w-3.5" /> Reparaturen</TabsTrigger>
          <TabsTrigger value="costs" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-costs"><Calculator className="h-3.5 w-3.5" /> Kosten</TabsTrigger>
          <TabsTrigger value="thresholds" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-thresholds"><Clock className="h-3.5 w-3.5" /> Sanierung</TabsTrigger>
          <TabsTrigger value="visibility" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-visibility"><Eye className="h-3.5 w-3.5" /> Sichtbarkeit</TabsTrigger>
          <TabsTrigger value="privacy" className="gap-1.5 text-xs px-2 py-2" data-testid="tab-privacy"><FileText className="h-3.5 w-3.5" /> Datenschutz</TabsTrigger>
        </TabsList>

        <TabsContent value="fees" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Gestaffelte Grundvergütung</CardTitle>
              <CardDescription>Vergütungssätze je nach Anzahl der Einheiten. Sie können die Bereiche und Sätze anpassen.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Pos.</TableHead>
                      <TableHead className="w-48">Größenkategorie</TableHead>
                      <TableHead className="w-32">Von</TableHead>
                      <TableHead className="w-32">Bis</TableHead>
                      <TableHead className="w-40">€/Einheit/Monat</TableHead>
                      <TableHead className="w-40">Beispiel (10 Einh.)</TableHead>
                      <TableHead className="w-20 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(settings.fee_tiers_config || [
                      { key: 'kleinstgemeinschaft_mini', label: 'Kleinstgemeinschaft', min: 2, max: 3, rate: 55 },
                      { key: 'kleinstgemeinschaft', label: 'Kleine Gemeinschaft', min: 4, max: 6, rate: 50 },
                      { key: 'klein', label: 'Kleine WEG', min: 7, max: 15, rate: 40 },
                      { key: 'mittel', label: 'Mittlere WEG', min: 16, max: 30, rate: 35 },
                      { key: 'gross', label: 'Große WEG', min: 31, max: 60, rate: 30 },
                      { key: 'sehr_gross', label: 'Sehr große WEG', min: 61, max: 100, rate: 27 },
                      { key: 'grossanlage', label: 'Großanlage', min: 101, max: 999, rate: 25 },
                    ]).map((tier, idx, arr) => (
                      <TableRow key={tier.key}>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <button disabled={idx === 0} onClick={() => moveItem('fee_tiers_config', idx, -1)} className="p-0.5 hover:bg-secondary rounded disabled:opacity-20" data-testid={`tier-up-${idx}`}>
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button disabled={idx === arr.length - 1} onClick={() => moveItem('fee_tiers_config', idx, 1)} className="p-0.5 hover:bg-secondary rounded disabled:opacity-20" data-testid={`tier-down-${idx}`}>
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="text"
                            className="w-40 h-8"
                            value={tier.label}
                            onChange={e => {
                              const newConfig = [...(settings.fee_tiers_config || [
                                { key: 'kleinstgemeinschaft_mini', label: 'Kleinstgemeinschaft', min: 2, max: 3, rate: 55 },
                                { key: 'kleinstgemeinschaft', label: 'Kleine Gemeinschaft', min: 4, max: 6, rate: 50 },
                                { key: 'klein', label: 'Kleine WEG', min: 7, max: 15, rate: 40 },
                                { key: 'mittel', label: 'Mittlere WEG', min: 16, max: 30, rate: 35 },
                                { key: 'gross', label: 'Große WEG', min: 31, max: 60, rate: 30 },
                                { key: 'sehr_gross', label: 'Sehr große WEG', min: 61, max: 100, rate: 27 },
                                { key: 'grossanlage', label: 'Großanlage', min: 101, max: 999, rate: 25 },
                              ])];
                              newConfig[idx] = { ...newConfig[idx], label: e.target.value };
                              setSettings({ ...settings, fee_tiers_config: newConfig });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            className="w-20 h-8"
                            min="2"
                            value={tier.min}
                            onChange={e => {
                              const newConfig = [...(settings.fee_tiers_config || [
                                { key: 'kleinstgemeinschaft_mini', label: 'Kleinstgemeinschaft', min: 2, max: 3, rate: 55 },
                                { key: 'kleinstgemeinschaft', label: 'Kleine Gemeinschaft', min: 4, max: 6, rate: 50 },
                                { key: 'klein', label: 'Kleine WEG', min: 7, max: 15, rate: 40 },
                                { key: 'mittel', label: 'Mittlere WEG', min: 16, max: 30, rate: 35 },
                                { key: 'gross', label: 'Große WEG', min: 31, max: 60, rate: 30 },
                                { key: 'sehr_gross', label: 'Sehr große WEG', min: 61, max: 100, rate: 27 },
                                { key: 'grossanlage', label: 'Großanlage', min: 101, max: 999, rate: 25 },
                              ])];
                              newConfig[idx] = { ...newConfig[idx], min: parseInt(e.target.value) || 2 };
                              setSettings({ ...settings, fee_tiers_config: newConfig });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            className="w-20 h-8"
                            min="2"
                            value={tier.max}
                            onChange={e => {
                              const newConfig = [...(settings.fee_tiers_config || [
                                { key: 'kleinstgemeinschaft_mini', label: 'Kleinstgemeinschaft', min: 2, max: 3, rate: 55 },
                                { key: 'kleinstgemeinschaft', label: 'Kleine Gemeinschaft', min: 4, max: 6, rate: 50 },
                                { key: 'klein', label: 'Kleine WEG', min: 7, max: 15, rate: 40 },
                                { key: 'mittel', label: 'Mittlere WEG', min: 16, max: 30, rate: 35 },
                                { key: 'gross', label: 'Große WEG', min: 31, max: 60, rate: 30 },
                                { key: 'sehr_gross', label: 'Sehr große WEG', min: 61, max: 100, rate: 27 },
                                { key: 'grossanlage', label: 'Großanlage', min: 101, max: 999, rate: 25 },
                              ])];
                              newConfig[idx] = { ...newConfig[idx], max: parseInt(e.target.value) || 999 };
                              setSettings({ ...settings, fee_tiers_config: newConfig });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.50"
                            className="w-24 h-8"
                            value={tier.rate}
                            onChange={e => {
                              const newConfig = [...(settings.fee_tiers_config || [
                                { key: 'kleinstgemeinschaft_mini', label: 'Kleinstgemeinschaft', min: 2, max: 3, rate: 55 },
                                { key: 'kleinstgemeinschaft', label: 'Kleine Gemeinschaft', min: 4, max: 6, rate: 50 },
                                { key: 'klein', label: 'Kleine WEG', min: 7, max: 15, rate: 40 },
                                { key: 'mittel', label: 'Mittlere WEG', min: 16, max: 30, rate: 35 },
                                { key: 'gross', label: 'Große WEG', min: 31, max: 60, rate: 30 },
                                { key: 'sehr_gross', label: 'Sehr große WEG', min: 61, max: 100, rate: 27 },
                                { key: 'grossanlage', label: 'Großanlage', min: 101, max: 999, rate: 25 },
                              ])];
                              newConfig[idx] = { ...newConfig[idx], rate: parseFloat(e.target.value) || 0 };
                              setSettings({ ...settings, fee_tiers_config: newConfig });
                            }}
                            data-testid={`fee-tier-${tier.key}`}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {(tier.rate * 10).toFixed(2)} €
                        </TableCell>
                        <TableCell className="text-right">
                          <button onClick={() => {
                            const newConfig = [...(settings.fee_tiers_config || [])].filter((_, i) => i !== idx);
                            setSettings({ ...settings, fee_tiers_config: newConfig });
                          }} className="p-1.5 hover:bg-secondary rounded text-red-500" data-testid={`tier-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex gap-2 mt-2">
                <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-tiers-btn">
                  <Save className="h-4 w-4" /> Staffelung speichern
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const newConfig = [...(settings.fee_tiers_config || [
                      { key: 'kleinstgemeinschaft_mini', label: 'Kleinstgemeinschaft', min: 2, max: 3, rate: 55 },
                      { key: 'kleinstgemeinschaft', label: 'Kleine Gemeinschaft', min: 4, max: 6, rate: 50 },
                      { key: 'klein', label: 'Kleine WEG', min: 7, max: 15, rate: 40 },
                      { key: 'mittel', label: 'Mittlere WEG', min: 16, max: 30, rate: 35 },
                      { key: 'gross', label: 'Große WEG', min: 31, max: 60, rate: 30 },
                      { key: 'sehr_gross', label: 'Sehr große WEG', min: 61, max: 100, rate: 27 },
                      { key: 'grossanlage', label: 'Großanlage', min: 101, max: 999, rate: 25 },
                    ])];
                    const lastTier = newConfig[newConfig.length - 1];
                    newConfig.push({ 
                      key: `custom_${Date.now()}`, 
                      label: 'Neue Kategorie', 
                      min: lastTier.max + 1, 
                      max: lastTier.max + 50, 
                      rate: lastTier.rate - 2 
                    });
                    setSettings({ ...settings, fee_tiers_config: newConfig });
                  }}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" /> Kategorie hinzufügen
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Allgemeine Vergütungseinstellungen</CardTitle>
              <CardDescription>Stundensatz und alternative Berechnungsmethoden</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Einheitlicher Grundhonorar-Satz (überschreibt Staffelung)</Label>
                    <Switch
                      checked={settings.base_fee_per_unit_active === true}
                      onCheckedChange={v => setSettings({ ...settings, base_fee_per_unit_active: v })}
                      data-testid="toggle-base-fee-active"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {settings.base_fee_per_unit_active
                      ? 'Aktiv - Dieser Wert überschreibt die gestaffelte Grundvergütung'
                      : 'Deaktiviert - Es gilt die gestaffelte Grundvergütung'}
                  </p>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.base_fee_per_unit ?? 0}
                    disabled={!settings.base_fee_per_unit_active}
                    className={!settings.base_fee_per_unit_active ? 'opacity-50' : ''}
                    onChange={e => setSettings({ ...settings, base_fee_per_unit: parseFloat(e.target.value) })}
                    data-testid="input-base-fee"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stundensatz (EUR netto)</Label>
                  <Input type="number" step="0.01" value={settings.hourly_rate} onChange={e => setSettings({ ...settings, hourly_rate: parseFloat(e.target.value) || 0 })} data-testid="input-hourly-rate" />
                </div>
              </div>
              <div className="p-4 border bg-amber-50/50 border-amber-200 rounded-lg space-y-3">
                <Label className="text-sm font-semibold">Mindesthonorar (EUR netto/Monat gesamt)</Label>
                <p className="text-xs text-muted-foreground">Monatliches Mindesthonorar für die gesamte Liegenschaft. Greift automatisch, wenn die berechnete Vergütung darunter liegt. Bei besonders günstiger Bewertung kann das Mindesthonorar um den eingestellten Prozentsatz unterschritten werden.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Mindesthonorar</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="10"
                        min="0"
                        value={settings.minimum_fee ?? 300}
                        onChange={e => setSettings({ ...settings, minimum_fee: parseFloat(e.target.value) || 0 })}
                        className="w-32"
                        data-testid="input-minimum-fee"
                      />
                      <span className="text-sm text-muted-foreground">€ / Monat</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Max. Reduktion bei günstiger Bewertung</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="30"
                        value={settings.minimum_fee_reduction_percent ?? 10}
                        onChange={e => setSettings({ ...settings, minimum_fee_reduction_percent: parseFloat(e.target.value) || 0 })}
                        className="w-24"
                        data-testid="input-minimum-fee-reduction"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                </div>
                {(settings.minimum_fee ?? 300) > 0 && (
                  <p className="text-xs text-amber-700">
                    Mindesthonorar: {settings.minimum_fee ?? 300} € · Bei günstiger Bewertung min. {Math.floor((settings.minimum_fee ?? 300) * (1 - (settings.minimum_fee_reduction_percent ?? 10) / 100))} €
                  </p>
                )}
              </div>
              <Separator className="my-4" />
              <div className="space-y-2">
                <Label htmlFor="renovation-fee-percent">Vergütungssatz für Modernisierungen (%)</Label>
                <p className="text-xs text-muted-foreground mb-2">Prozentsatz des Modernisierungsvolumens, der als zusätzliche Verwaltervergütung berechnet wird</p>
                <div className="flex items-center gap-2">
                  <Input 
                    id="renovation-fee-percent"
                    type="number" 
                    step="0.5" 
                    min="0"
                    max="20"
                    value={settings.renovation_fee_percent || 5} 
                    onChange={e => setSettings({ ...settings, renovation_fee_percent: parseFloat(e.target.value) || 5 })} 
                    className="w-24"
                    data-testid="input-renovation-fee-percent" 
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
              </div>
              <Button onClick={saveSettings} disabled={saving} className="gap-2 mt-4" data-testid="save-fees-btn">
                <Save className="h-4 w-4" /> {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Modernisierungsgewichtung</CardTitle>
              <CardDescription>Wie stark verbessert jede durchgeführte Modernisierung den Gebäudezustand-Score? Höhere Werte = stärkere Verbesserung.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {[
                  { key: 'heating', label: 'Erneuerung Heizungsanlage' },
                  { key: 'water_pipes', label: 'Erneuerung Wasserleitungen' },
                  { key: 'facade_paint', label: 'Fassadenanstrich' },
                  { key: 'insulation', label: 'Wärmedämmung' },
                  { key: 'basement_sealing', label: 'Kellerabdichtung' },
                  { key: 'windows', label: 'Fenster' },
                  { key: 'roof', label: 'Dachsanierung' },
                  { key: 'elevator_modernization', label: 'Aufzugsmodernisierung' },
                  { key: 'electrical', label: 'Elektroinstallation' },
                  { key: 'balconies', label: 'Balkonsanierung' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-dashed last:border-0">
                    <span className="text-sm">{label}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={settings.modernization_weights?.[key] ?? 8}
                        onChange={e => setSettings({
                          ...settings,
                          modernization_weights: {
                            ...settings.modernization_weights,
                            [key]: parseInt(e.target.value) || 0,
                          }
                        })}
                        className="h-8 text-sm w-16"
                        data-testid={`mod-weight-${key}`}
                      />
                      <span className="text-xs text-muted-foreground w-12">Punkte</span>
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={saveSettings} disabled={saving} className="gap-2 mt-4" data-testid="save-mod-weights-btn">
                <Save className="h-4 w-4" /> Alle Änderungen speichern
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Zusatzdienstleistungen</CardTitle>
                  <CardDescription>Preise für einzelne Zusatzleistungen verwalten</CardDescription>
                </div>
                <Button onClick={openAddServiceDialog} className="gap-2" data-testid="add-service-btn">
                  <Plus className="h-4 w-4" /> Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Status</TableHead>
                      <TableHead>Leistung</TableHead>
                      <TableHead className="w-28">Preis (EUR)</TableHead>
                      <TableHead className="w-36">Einheit</TableHead>
                      <TableHead className="w-28 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {settings.additional_services?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Keine Zusatzleistungen definiert. Klicken Sie auf "Hinzufügen" um eine neue Leistung anzulegen.
                        </TableCell>
                      </TableRow>
                    )}
                    {settings.additional_services?.map((s, i, arr) => (
                      <TableRow key={i} className={s.active === false ? 'opacity-50' : ''}>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleServiceActive(i)}
                            className="h-8 w-8 p-0"
                            data-testid={`service-toggle-${i}`}
                          >
                            {s.active !== false ? (
                              <ToggleRight className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{s.name}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={s.price}
                            onChange={e => updateService(i, 'price', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm w-24"
                            data-testid={`service-price-${i}`}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.unit}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button disabled={i === 0} onClick={() => moveItem('additional_services', i, -1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronUp className="h-3.5 w-3.5" /></button>
                            <button disabled={i === arr.length - 1} onClick={() => moveItem('additional_services', i, 1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronDown className="h-3.5 w-3.5" /></button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditServiceDialog(i)}
                              className="h-8 w-8 p-0"
                              data-testid={`service-edit-${i}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteService(i)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`service-delete-${i}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button onClick={saveSettings} disabled={saving} className="gap-2 mt-4" data-testid="save-services-btn">
                <Save className="h-4 w-4" /> Alle Änderungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ausstattung & Besonderheiten Tab */}
        <TabsContent value="equipment" className="space-y-6">
          {/* Gebäude/Treppenhäuser Komplexitätswertung - CRUD */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Gebäude / Treppenhäuser - Komplexitätswertung
                </span>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => {
                  setBuildingForm({ key: '', label: '', type: 'per_building', points: 0, max_points: 15, threshold: 10, active: true, description: '' });
                  setEditingBuildingIndex(null);
                  setBuildingDialog(true);
                }} data-testid="add-building-rule-btn">
                  <Plus className="h-3.5 w-3.5" /> Regel hinzufügen
                </Button>
              </CardTitle>
              <CardDescription>
                Regeln für die Bewertung der Gebäudekomplexität. Regeltypen: <strong>Pro Gebäude</strong> (multipliziert mit Anzahl), <strong>Schwellenwert unter/über</strong> (wenn Einheiten/Gebäude unter/über einem Wert liegt).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Bezeichnung</TableHead>
                    <TableHead className="w-36">Regeltyp</TableHead>
                    <TableHead className="w-20 text-center">Punkte</TableHead>
                    <TableHead className="w-24 text-center">Max/Schwelle</TableHead>
                    <TableHead className="w-16 text-center">Aktiv</TableHead>
                    <TableHead className="w-24 text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getBuildingItems().map((item, idx) => (
                    <TableRow key={item.key || idx} className={!item.active ? 'opacity-50' : ''}>
                      <TableCell className="p-1">
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => moveBuildingItem(idx, -1)} disabled={idx === 0} className="p-0.5 hover:bg-secondary rounded disabled:opacity-30" data-testid={`building-up-${idx}`}>
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button onClick={() => moveBuildingItem(idx, 1)} disabled={idx === getBuildingItems().length - 1} className="p-0.5 hover:bg-secondary rounded disabled:opacity-30" data-testid={`building-down-${idx}`}>
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium text-sm">{item.label}</span>
                          {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.type === 'per_building' ? 'Pro Gebäude' : item.type === 'threshold_below' ? 'Schwelle unter' : 'Schwelle über'}
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">{item.points}</TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {item.type === 'per_building' ? `max ${item.max_points}` : item.threshold}
                      </TableCell>
                      <TableCell className="text-center">
                        <button onClick={() => toggleBuildingActive(idx)} className="text-lg" data-testid={`building-toggle-${idx}`}>
                          {item.active ? <ToggleRight className="h-5 w-5 text-green-500" /> : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => {
                            setBuildingForm({ ...item });
                            setEditingBuildingIndex(idx);
                            setBuildingDialog(true);
                          }} className="p-1.5 hover:bg-secondary rounded" data-testid={`building-edit-${idx}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteBuildingItem(idx)} className="p-1.5 hover:bg-secondary rounded text-red-500" data-testid={`building-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getBuildingItems().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                        Keine Regeln definiert. Klicken Sie auf "Regel hinzufügen".
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="pt-2">
                <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-building-complexity-btn">
                  <Save className="h-4 w-4" /> Gebäudekomplexität speichern
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Ausstattung & Besonderheiten - Komplexitätswertung</span>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={openAddEquipmentDialog} data-testid="add-equipment-btn">
                  <Plus className="h-3.5 w-3.5" /> Hinzufügen
                </Button>
              </CardTitle>
              <CardDescription>
                Definieren Sie, wie stark verschiedene Ausstattungsmerkmale die Komplexität und damit die Vergütungsempfehlung beeinflussen. 
                Positive Werte erhöhen die Komplexität, negative Werte verringern sie. Sie können neue Merkmale hinzufügen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="w-16">Pos.</TableHead>
                    <TableHead className="w-56">Ausstattungsmerkmal</TableHead>
                    <TableHead className="w-36">Komplexitätspunkte</TableHead>
                    <TableHead>Beschreibung</TableHead>
                    <TableHead className="w-24 text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getEquipmentItems().map((item, idx, arr) => (
                    <TableRow key={item.key || idx} className={item.active === false ? 'opacity-50' : ''}>
                      <TableCell>
                        <button onClick={() => toggleEquipmentActive(idx)} className="text-muted-foreground hover:text-foreground" data-testid={`equipment-toggle-${idx}`}>
                          {item.active !== false ? <ToggleRight className="h-4 w-4 text-emerald-600" /> : <ToggleLeft className="h-4 w-4" />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <button disabled={idx === 0} onClick={() => moveEquipmentItem(idx, -1)} className="p-0.5 hover:bg-secondary rounded disabled:opacity-20" data-testid={`equipment-up-${idx}`}>
                            <ChevronUp className="h-3.5 w-3.5" />
                          </button>
                          <button disabled={idx === arr.length - 1} onClick={() => moveEquipmentItem(idx, 1)} className="p-0.5 hover:bg-secondary rounded disabled:opacity-20" data-testid={`equipment-down-${idx}`}>
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{item.label}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className="w-24 h-8"
                          value={item.weight}
                          onChange={e => updateEquipmentWeight(idx, e.target.value)}
                          data-testid={`equipment-weight-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.description || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEditEquipmentDialog(idx)} className="p-1.5 hover:bg-secondary rounded" data-testid={`equipment-edit-${idx}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteEquipmentItem(idx)} className="p-1.5 hover:bg-secondary rounded text-red-500" data-testid={`equipment-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="pt-2">
                <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-equipment-btn">
                  <Save className="h-4 w-4" /> Ausstattungswertung speichern
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gebäudezustand-Berechnung Tab */}
        <TabsContent value="condition" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Gebäudezustand - Berechnungsparameter
              </CardTitle>
              <CardDescription>
                Der Gebäudezustand-Score (0-100) fließt mit <strong>35% Gewichtung</strong> in die Gesamtbewertung ein. Höherer Score = mehr Verwaltungsaufwand. Hier können Sie alle Faktoren der Berechnung einstellen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 1. Altersberechnung */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">1. Altersberechnung (Hauptfaktor)</h4>
                <p className="text-xs text-muted-foreground">
                  Formel: <code className="bg-muted px-1 rounded">Score = Min + (Alter / Max-Jahre) × (Max - Min)</code><br />
                  Beispiel mit aktuellen Werten: Gebäude 30 Jahre alt → Score = {(settings.building_condition_params?.age_min_score ?? 10)} + (30 / {(settings.building_condition_params?.age_max_years ?? 60)}) × {((settings.building_condition_params?.age_max_score ?? 85) - (settings.building_condition_params?.age_min_score ?? 10))} = <strong>{Math.round((settings.building_condition_params?.age_min_score ?? 10) + (30 / (settings.building_condition_params?.age_max_years ?? 60)) * ((settings.building_condition_params?.age_max_score ?? 85) - (settings.building_condition_params?.age_min_score ?? 10)))}</strong>
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Min. Score (Neubau)</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.age_min_score ?? 10}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), age_min_score: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-age-min-score"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Score bei Alter = 0</p>
                  </div>
                  <div>
                    <Label className="text-xs">Max. Score (Altbau)</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.age_max_score ?? 85}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), age_max_score: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-age-max-score"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Max. Score bei altem Gebäude</p>
                  </div>
                  <div>
                    <Label className="text-xs">Max. Jahre</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.age_max_years ?? 60}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), age_max_years: parseFloat(e.target.value) || 60}})}
                      data-testid="bcp-age-max-years"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Ab diesem Alter: Max-Score</p>
                  </div>
                </div>
              </div>

              {/* 2. Allgemeinzustand */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">2. Allgemeinzustand (Auf-/Abschlag)</h4>
                <p className="text-xs text-muted-foreground">
                  Wird zum Alters-Score addiert. Negative Werte = Abzug (verbessernd), Positive Werte = Zuschlag (verschlechternd).
                </p>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Sehr gut</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.condition_sehr_gut ?? -10}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), condition_sehr_gut: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-condition-sehr-gut"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Gut</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.condition_gut ?? 0}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), condition_gut: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-condition-gut"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mittel</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.condition_mittel ?? 10}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), condition_mittel: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-condition-mittel"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Schlecht</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.condition_schlecht ?? 20}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), condition_schlecht: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-condition-schlecht"
                    />
                  </div>
                </div>
              </div>

              {/* 3. Baumängel */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">3. Baumängel bei Neubauten</h4>
                <p className="text-xs text-muted-foreground">
                  Strafzuschlag wird nur angewendet, wenn das Gebäude jünger als das eingestellte Höchstalter ist UND Baumängel vorhanden sind.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Strafzuschlag (Punkte)</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.defect_penalty ?? 25}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), defect_penalty: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-defect-penalty"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Höchstalter (Jahre)</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.defect_max_age ?? 6}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), defect_max_age: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-defect-max-age"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Nur bei Gebäuden jünger als X Jahre</p>
                  </div>
                </div>
              </div>

              {/* 4. Modernisierungen & Reparaturen */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">4. Modernisierungen & Reparaturen (Standard-Gewichte)</h4>
                <p className="text-xs text-muted-foreground">
                  Standard-Werte, wenn für ein Item kein spezifisches Gewicht konfiguriert ist. Einzelne Gewichte konfigurieren Sie in den Tabs "Modern." und "Reparaturen".
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Modernisierung: Abzug pro Item</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.default_modernization_reduction ?? 8}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), default_modernization_reduction: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-default-mod-reduction"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Reduziert Score (verbessernd)</p>
                  </div>
                  <div>
                    <Label className="text-xs">Reparatur: Zuschlag pro Item</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.default_repair_weight ?? 3}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), default_repair_weight: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-default-repair-weight"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Erhöht Score (verschlechternd)</p>
                  </div>
                </div>
              </div>

              {/* 5. Geplante Sanierung & Sanierungsbedarf */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">5. Geplante Sanierungen & Sanierungsbedarf</h4>
                <p className="text-xs text-muted-foreground">
                  Zuschlag wenn Sanierungen geplant sind, plus Zuschlag pro kommendem Sanierungsbedarf (Schwellenwerte im Tab "Sanierung").
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Geplante Sanierung (Pauschal-Zuschlag)</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.planned_renovation_penalty ?? 10}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), planned_renovation_penalty: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-planned-penalty"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Wenn mind. 1 Sanierung geplant</p>
                  </div>
                  <div>
                    <Label className="text-xs">Sanierungsbedarf (pro Bedarf)</Label>
                    <Input
                      type="number"
                      className="h-8"
                      value={settings.building_condition_params?.upcoming_need_penalty ?? 5}
                      onChange={e => setSettings({...settings, building_condition_params: {...(settings.building_condition_params || {}), upcoming_need_penalty: parseFloat(e.target.value) || 0}})}
                      data-testid="bcp-upcoming-penalty"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Pro kommendem Bedarf (automatisch berechnet)</p>
                  </div>
                </div>
              </div>

              {/* Live Preview */}
              <div className="p-4 border-2 border-primary/20 rounded-lg bg-primary/5 space-y-2">
                <h4 className="font-semibold text-sm">Vorschau: Beispielberechnung</h4>
                <div className="grid grid-cols-3 gap-4 text-xs">
                  {[10, 30, 50].map(exAge => {
                    const bcp = settings.building_condition_params || {};
                    const ageMin = bcp.age_min_score ?? 10;
                    const ageMax = bcp.age_max_score ?? 85;
                    const ageYears = bcp.age_max_years ?? 60;
                    const score = exAge >= ageYears ? ageMax : ageMin + (exAge / ageYears) * (ageMax - ageMin);
                    const withGut = score + (bcp.condition_gut ?? 0);
                    const withSehrGut = score + (bcp.condition_sehr_gut ?? -10);
                    return (
                      <div key={exAge} className="p-2 bg-background rounded border">
                        <p className="font-semibold mb-1">Gebäude {exAge} Jahre alt:</p>
                        <p>Basis-Score: <strong>{Math.round(score)}</strong></p>
                        <p>+ "Gut": <strong>{Math.round(withGut)}</strong></p>
                        <p>+ "Sehr gut": <strong>{Math.round(withSehrGut)}</strong></p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pt-2">
                <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-condition-params-btn">
                  <Save className="h-4 w-4" /> Gebäudezustand-Parameter speichern
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="formulas">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Berechnungsparameter für Zusatzkosten</CardTitle>
              <CardDescription>
                Konfigurieren Sie die Faktoren und Basiswerte für die Berechnung der geschätzten Zusatzvergütung basierend auf der Verwaltungshistorie.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Außerordentliche Versammlungen */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">Außerordentliche Versammlungen</h4>
                <p className="text-xs text-muted-foreground">Formel: Basiswert × Einheiten × Faktor</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Basiswert (€)</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.extraordinary_meetings_base ?? 30}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, extraordinary_meetings_base: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-ext-meetings-base"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Häufig"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.extraordinary_meetings_haeufig_factor ?? 2.0}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, extraordinary_meetings_haeufig_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-ext-meetings-haeufig"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Selten"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.extraordinary_meetings_selten_factor ?? 0.5}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, extraordinary_meetings_selten_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-ext-meetings-selten"
                    />
                  </div>
                </div>
              </div>

              {/* Eigentümerwechsel */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">Eigentümerwechsel</h4>
                <p className="text-xs text-muted-foreground">Formel: Basiswert × max(Einheiten × Faktor, 1)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Basiswert (€)</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.ownership_changes_base ?? 100}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, ownership_changes_base: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-ownership-base"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Häufig"</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.fee_calculation_params?.ownership_changes_haeufig_factor ?? 0.2}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, ownership_changes_haeufig_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-ownership-haeufig"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Selten"</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.fee_calculation_params?.ownership_changes_selten_factor ?? 0.05}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, ownership_changes_selten_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-ownership-selten"
                    />
                  </div>
                </div>
              </div>

              {/* Zahlungsausfälle */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">Zahlungsausfälle / Mahnungen</h4>
                <p className="text-xs text-muted-foreground">Formel: Basiswert × max(Einheiten × Faktor, 1) [× Multiplikator bei häufig]</p>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Basiswert (€)</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.payment_defaults_base ?? 25}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, payment_defaults_base: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-payment-base"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Häufig"</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.fee_calculation_params?.payment_defaults_haeufig_factor ?? 0.3}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, payment_defaults_haeufig_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-payment-haeufig"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Multiplikator "Häufig"</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.payment_defaults_haeufig_multiplier ?? 3}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, payment_defaults_haeufig_multiplier: parseInt(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-payment-multiplier"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Selten"</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.fee_calculation_params?.payment_defaults_selten_factor ?? 0.1}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, payment_defaults_selten_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-payment-selten"
                    />
                  </div>
                </div>
              </div>

              {/* Sonderumlagen */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">Sonderumlagen</h4>
                <p className="text-xs text-muted-foreground">Formel: Basiswert × Einheiten × Faktor</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Basiswert (€)</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.special_assessments_base ?? 25}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, special_assessments_base: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-special-base"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Häufig"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.special_assessments_haeufig_factor ?? 2.0}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, special_assessments_haeufig_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-special-haeufig"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Selten"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.special_assessments_selten_factor ?? 0.5}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, special_assessments_selten_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-special-selten"
                    />
                  </div>
                </div>
              </div>

              {/* Gerichtsverfahren */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">Gerichtsverfahren</h4>
                <p className="text-xs text-muted-foreground">Formel: Stundensatz × Stunden</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Stunden "Häufig"</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.court_proceedings_haeufig_hours ?? 10}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, court_proceedings_haeufig_hours: parseInt(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-court-haeufig"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Stunden "Selten"</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.court_proceedings_selten_hours ?? 3}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, court_proceedings_selten_hours: parseInt(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-court-selten"
                    />
                  </div>
                </div>
              </div>

              {/* SEPA-Teilnahme */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">SEPA-Teilnahme</h4>
                <p className="text-xs text-muted-foreground">Formel: Basiswert × Einheiten × Faktor × 12 (jährlich)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Basiswert (€ je Vorgang)</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.sepa_base ?? 5}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, sepa_base: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-sepa-base"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Wenige"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.sepa_wenige_factor ?? 0.5}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, sepa_wenige_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-sepa-wenige"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Meiste"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.sepa_meiste_factor ?? 0.1}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, sepa_meiste_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-sepa-meiste"
                    />
                  </div>
                </div>
              </div>

              {/* Versicherungsschäden */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">Versicherungsschäden</h4>
                <p className="text-xs text-muted-foreground">Formel: Stundensatz × Stunden (nur bei "Häufig")</p>
                <div className="w-48">
                  <Label className="text-xs">Stunden "Häufig"</Label>
                  <Input
                    type="number"
                    value={settings.fee_calculation_params?.insurance_claims_hours ?? 5}
                    onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, insurance_claims_hours: parseInt(e.target.value) || 0}})}
                    className="h-8"
                    data-testid="param-insurance-hours"
                  />
                </div>
              </div>

              {/* Umlaufbeschlüsse */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm">Umlaufbeschlüsse</h4>
                <p className="text-xs text-muted-foreground">Formel: Basiswert × Einheiten × Faktor</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Basiswert (€)</Label>
                    <Input
                      type="number"
                      value={settings.fee_calculation_params?.circular_resolutions_base ?? 25}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, circular_resolutions_base: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-circular-base"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Häufig"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.circular_resolutions_haeufig_factor ?? 2.0}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, circular_resolutions_haeufig_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-circular-haeufig"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Faktor "Selten"</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fee_calculation_params?.circular_resolutions_selten_factor ?? 0.5}
                      onChange={e => setSettings({...settings, fee_calculation_params: {...settings.fee_calculation_params, circular_resolutions_selten_factor: parseFloat(e.target.value) || 0}})}
                      className="h-8"
                      data-testid="param-circular-selten"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-formulas-btn">
                <Save className="h-4 w-4" /> Alle Änderungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assessments">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Bewertungslabels & Farben
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie die Score-Schwellenwerte, Bezeichnungen und Farben für die verschiedenen Bewertungsstufen.
                Der Gesamtscore (0-100) wird basierend auf diesen Schwellenwerten einer Kategorie zugeordnet.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {getRatingLabels().length} Bewertungsstufen definiert
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={resetRatingLabels} data-testid="reset-rating-labels">
                    Zurücksetzen
                  </Button>
                  <Button size="sm" onClick={openAddRatingDialog} className="gap-1" data-testid="add-rating-label">
                    <Plus className="h-4 w-4" /> Hinzufügen
                  </Button>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Aktiv</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="text-center">Score-Bereich</TableHead>
                    <TableHead className="text-center">Bedarfs-%</TableHead>
                    <TableHead>Farbe</TableHead>
                    <TableHead className="w-24 text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getRatingLabels().map((item, idx) => (
                    <TableRow key={item.key || idx} className={item.active === false ? 'opacity-50' : ''}>
                      <TableCell>
                        <Switch
                          checked={item.active !== false}
                          onCheckedChange={() => toggleRatingActive(idx)}
                          data-testid={`rating-active-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{item.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">({item.key})</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            className="w-16 h-8 text-center font-mono text-sm"
                            value={item.min_score}
                            onChange={e => {
                              const labels = [...getRatingLabels()];
                              labels[idx] = { ...labels[idx], min_score: parseInt(e.target.value) || 0 };
                              setSettings({ ...settings, rating_labels: labels });
                            }}
                            data-testid={`rating-min-score-${idx}`}
                          />
                          <span className="text-muted-foreground">-</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            className="w-16 h-8 text-center font-mono text-sm"
                            value={item.max_score}
                            onChange={e => {
                              const labels = [...getRatingLabels()];
                              labels[idx] = { ...labels[idx], max_score: parseInt(e.target.value) || 100 };
                              setSettings({ ...settings, rating_labels: labels });
                            }}
                            data-testid={`rating-max-score-${idx}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          min="0"
                          max="300"
                          className="w-20 h-8 text-center font-mono text-sm mx-auto"
                          value={item.demand_pct}
                          onChange={e => {
                            const labels = [...getRatingLabels()];
                            labels[idx] = { ...labels[idx], demand_pct: parseInt(e.target.value) || 100 };
                            setSettings({ ...settings, rating_labels: labels });
                          }}
                          data-testid={`rating-demand-pct-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center gap-2 px-2 py-1 rounded text-sm ${getColorClasses(item.color).bg} ${getColorClasses(item.color).text}`}>
                          <span className={`w-3 h-3 rounded-full ${getColorClasses(item.color).border} border`}></span>
                          {COLOR_OPTIONS.find(c => c.value === item.color)?.label || item.color}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => openEditRatingDialog(idx)}
                            className="p-1.5 hover:bg-secondary rounded"
                            data-testid={`rating-edit-${idx}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteRatingItem(idx)}
                            className="p-1.5 hover:bg-secondary rounded text-red-500"
                            data-testid={`rating-delete-${idx}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex justify-end mt-3">
                <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-rating-labels-btn">
                  <Save className="h-4 w-4" />
                  {saving ? 'Speichern...' : 'Bewertungsstufen speichern'}
                </Button>
              </div>

              <div className="p-4 bg-secondary/50 rounded-lg space-y-4">
                <h4 className="font-semibold text-sm mb-2">Score-Gewichtung</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Legen Sie fest, wie stark jede Kategorie in den Gesamtscore einfließt. Die Summe sollte 100% ergeben.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Gebäudekomplexität (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={settings.score_weights?.complexity ?? 33.33}
                      onChange={e => setSettings({...settings, score_weights: {...(settings.score_weights || {}), complexity: parseFloat(e.target.value) || 0}})}
                      className="h-8 text-sm"
                      data-testid="score-weight-complexity"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Gebäudezustand (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={settings.score_weights?.condition ?? 33.33}
                      onChange={e => setSettings({...settings, score_weights: {...(settings.score_weights || {}), condition: parseFloat(e.target.value) || 0}})}
                      className="h-8 text-sm"
                      data-testid="score-weight-condition"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Verwaltungshistorie (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={settings.score_weights?.history ?? 33.33}
                      onChange={e => setSettings({...settings, score_weights: {...(settings.score_weights || {}), history: parseFloat(e.target.value) || 0}})}
                      className="h-8 text-sm"
                      data-testid="score-weight-history"
                    />
                  </div>
                </div>
                {(() => {
                  const sum = (settings.score_weights?.complexity ?? 33.33) + (settings.score_weights?.condition ?? 33.33) + (settings.score_weights?.history ?? 33.33);
                  const rounded = Math.round(sum * 100) / 100;
                  return (
                    <p className={`text-xs font-medium ${rounded >= 99.99 && rounded <= 100.01 ? 'text-emerald-600' : 'text-red-600'}`}>
                      Summe: {rounded}% {(rounded < 99.99 || rounded > 100.01) && '(sollte 100% ergeben)'}
                    </p>
                  );
                })()}
                <p className="text-xs text-muted-foreground">
                  Der "Bedarfs-%" beeinflusst die empfohlene Gebühr (z.B. 150% = 50% höhere Gebühr als Basis).
                </p>
              </div>

              <Separator />

              <div className="p-4 bg-secondary/50 rounded-lg space-y-4">
                <h4 className="font-semibold text-sm mb-2">Vergütungsmultiplikatoren</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Definieren Sie, wie der Bedarfs-% die Vergütungsempfehlung beeinflusst. Für jeden Bedarfsbereich können Sie Min/Max-Multiplikatoren und die Bewertungskategorie festlegen.
                </p>
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_80px_80px_80px_80px_120px_40px] gap-2 text-xs font-medium text-muted-foreground px-1">
                    <span>Bezeichnung</span>
                    <span>Von %</span>
                    <span>Bis %</span>
                    <span>Min-Mult.</span>
                    <span>Max-Mult.</span>
                    <span>Bewertung</span>
                    <span></span>
                  </div>
                  {(settings.recommendation_tiers || []).map((tier, idx) => (
                    <div key={tier.key || idx} className="grid grid-cols-[1fr_80px_80px_80px_80px_120px_40px] gap-2 items-center">
                      <Input
                        value={tier.label || ''}
                        onChange={e => {
                          const tiers = [...(settings.recommendation_tiers || [])];
                          tiers[idx] = {...tiers[idx], label: e.target.value};
                          setSettings({...settings, recommendation_tiers: tiers});
                        }}
                        className="h-8 text-sm"
                        data-testid={`rec-tier-label-${idx}`}
                      />
                      <Input
                        type="number"
                        value={tier.min_demand ?? 0}
                        onChange={e => {
                          const tiers = [...(settings.recommendation_tiers || [])];
                          tiers[idx] = {...tiers[idx], min_demand: parseInt(e.target.value) || 0};
                          setSettings({...settings, recommendation_tiers: tiers});
                        }}
                        className="h-8 text-sm"
                        data-testid={`rec-tier-min-demand-${idx}`}
                      />
                      <Input
                        type="number"
                        value={tier.max_demand ?? 999}
                        onChange={e => {
                          const tiers = [...(settings.recommendation_tiers || [])];
                          tiers[idx] = {...tiers[idx], max_demand: parseInt(e.target.value) || 0};
                          setSettings({...settings, recommendation_tiers: tiers});
                        }}
                        className="h-8 text-sm"
                        data-testid={`rec-tier-max-demand-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={tier.min_multiplier ?? 1.0}
                        onChange={e => {
                          const tiers = [...(settings.recommendation_tiers || [])];
                          tiers[idx] = {...tiers[idx], min_multiplier: parseFloat(e.target.value) || 0};
                          setSettings({...settings, recommendation_tiers: tiers});
                        }}
                        className="h-8 text-sm"
                        data-testid={`rec-tier-min-mult-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={tier.max_multiplier ?? 1.0}
                        onChange={e => {
                          const tiers = [...(settings.recommendation_tiers || [])];
                          tiers[idx] = {...tiers[idx], max_multiplier: parseFloat(e.target.value) || 0};
                          setSettings({...settings, recommendation_tiers: tiers});
                        }}
                        className="h-8 text-sm"
                        data-testid={`rec-tier-max-mult-${idx}`}
                      />
                      <Select
                        value={tier.assessment || 'standard'}
                        onValueChange={v => {
                          const tiers = [...(settings.recommendation_tiers || [])];
                          tiers[idx] = {...tiers[idx], assessment: v};
                          setSettings({...settings, recommendation_tiers: tiers});
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`rec-tier-assessment-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reduziert">Reduziert</SelectItem>
                          <SelectItem value="standard">Standard</SelectItem>
                          <SelectItem value="erhoeht">Erhöht</SelectItem>
                          <SelectItem value="besonderer_bedarf">Besonderer Bedarf</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        onClick={() => {
                          const tiers = (settings.recommendation_tiers || []).filter((_, i) => i !== idx);
                          setSettings({...settings, recommendation_tiers: tiers});
                        }}
                        data-testid={`rec-tier-delete-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      const tiers = [...(settings.recommendation_tiers || []), {
                        key: `tier_${Date.now()}`,
                        label: 'Neue Stufe',
                        min_demand: 0,
                        max_demand: 100,
                        min_multiplier: 1.0,
                        max_multiplier: 1.0,
                        assessment: 'standard'
                      }];
                      setSettings({...settings, recommendation_tiers: tiers});
                    }}
                    data-testid="add-rec-tier-btn"
                  >
                    + Stufe hinzufügen
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold text-sm mb-3">Vertrags-Assessment Labels (Legacy)</h4>
                <p className="text-xs text-muted-foreground mb-4">
                  Diese Labels werden für die Vertragsempfehlung (Standard/Erhöht/Reduziert) verwendet.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Standard */}
                  <div className="p-3 border rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Standard</Label>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${getColorClasses(settings.assessment_labels?.standard?.color || 'blue').bg} ${getColorClasses(settings.assessment_labels?.standard?.color || 'blue').text}`}>
                        {settings.assessment_labels?.standard?.label || 'Standardvertrag'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={settings.assessment_labels?.standard?.label || settings.assessment_labels?.standard || 'Standardvertrag'}
                        onChange={(e) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, standard: { label: e.target.value, color: settings.assessment_labels?.standard?.color || 'blue' }}})}
                        className="h-8 text-sm"
                        data-testid="assessment-label-standard"
                      />
                      <Select value={settings.assessment_labels?.standard?.color || 'blue'} onValueChange={(v) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, standard: { label: settings.assessment_labels?.standard?.label || 'Standardvertrag', color: v }}})}>
                        <SelectTrigger className="w-24 h-8" data-testid="assessment-color-standard"><SelectValue /></SelectTrigger>
                        <SelectContent>{COLOR_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}><span className={`w-3 h-3 rounded-full ${c.bg} ${c.border} border inline-block`}></span></SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Erhöht */}
                  <div className="p-3 border rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Erhöht</Label>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${getColorClasses(settings.assessment_labels?.erhoeht?.color || 'amber').bg} ${getColorClasses(settings.assessment_labels?.erhoeht?.color || 'amber').text}`}>
                        {settings.assessment_labels?.erhoeht?.label || 'Erhöhter Aufwand'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={settings.assessment_labels?.erhoeht?.label || settings.assessment_labels?.erhoeht || 'Erhöhter Aufwand'}
                        onChange={(e) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, erhoeht: { label: e.target.value, color: settings.assessment_labels?.erhoeht?.color || 'amber' }}})}
                        className="h-8 text-sm"
                        data-testid="assessment-label-erhoeht"
                      />
                      <Select value={settings.assessment_labels?.erhoeht?.color || 'amber'} onValueChange={(v) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, erhoeht: { label: settings.assessment_labels?.erhoeht?.label || 'Erhöhter Aufwand', color: v }}})}>
                        <SelectTrigger className="w-24 h-8" data-testid="assessment-color-erhoeht"><SelectValue /></SelectTrigger>
                        <SelectContent>{COLOR_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}><span className={`w-3 h-3 rounded-full ${c.bg} ${c.border} border inline-block`}></span></SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Reduziert */}
                  <div className="p-3 border rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Reduziert</Label>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${getColorClasses(settings.assessment_labels?.reduziert?.color || 'emerald').bg} ${getColorClasses(settings.assessment_labels?.reduziert?.color || 'emerald').text}`}>
                        {settings.assessment_labels?.reduziert?.label || 'Günstige Konditionen'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={settings.assessment_labels?.reduziert?.label || settings.assessment_labels?.reduziert || 'Günstige Konditionen'}
                        onChange={(e) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, reduziert: { label: e.target.value, color: settings.assessment_labels?.reduziert?.color || 'emerald' }}})}
                        className="h-8 text-sm"
                        data-testid="assessment-label-reduziert"
                      />
                      <Select value={settings.assessment_labels?.reduziert?.color || 'emerald'} onValueChange={(v) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, reduziert: { label: settings.assessment_labels?.reduziert?.label || 'Günstige Konditionen', color: v }}})}>
                        <SelectTrigger className="w-24 h-8" data-testid="assessment-color-reduziert"><SelectValue /></SelectTrigger>
                        <SelectContent>{COLOR_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}><span className={`w-3 h-3 rounded-full ${c.bg} ${c.border} border inline-block`}></span></SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Kritisch */}
                  <div className="p-3 border rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Kritisch</Label>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${getColorClasses(settings.assessment_labels?.kritisch?.color || 'red').bg} ${getColorClasses(settings.assessment_labels?.kritisch?.color || 'red').text}`}>
                        {settings.assessment_labels?.kritisch?.label || 'Kritischer Bedarf'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={settings.assessment_labels?.kritisch?.label || settings.assessment_labels?.kritisch || 'Kritischer Bedarf'}
                        onChange={(e) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, kritisch: { label: e.target.value, color: settings.assessment_labels?.kritisch?.color || 'red' }}})}
                        className="h-8 text-sm"
                        data-testid="assessment-label-kritisch"
                      />
                      <Select value={settings.assessment_labels?.kritisch?.color || 'red'} onValueChange={(v) => setSettings({...settings, assessment_labels: { ...settings.assessment_labels, kritisch: { label: settings.assessment_labels?.kritisch?.label || 'Kritischer Bedarf', color: v }}})}>
                        <SelectTrigger className="w-24 h-8" data-testid="assessment-color-kritisch"><SelectValue /></SelectTrigger>
                        <SelectContent>{COLOR_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}><span className={`w-3 h-3 rounded-full ${c.bg} ${c.border} border inline-block`}></span></SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-assessments-btn">
                <Save className="h-4 w-4" /> Einstellungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="texts">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Texte & Hinweise</CardTitle>
              <CardDescription>
                Bearbeiten Sie die Texte, die auf der Startseite und bei der Analyse-Ergebnisseite angezeigt werden.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="hero-description" className="text-sm font-medium">Beschreibungstext auf der Startseite</Label>
                  <p className="text-xs text-muted-foreground">Der Beschreibungstext unterhalb der Überschrift auf der Startseite</p>
                  <Textarea
                    id="hero-description"
                    value={settings.homepage_link_text?.hero_description || 'Ermitteln Sie den Verwaltungsbedarf Ihrer Wohnungseigentümergemeinschaft. Unser Analysetool bewertet Gebäudezustand, Komplexität und Verwaltungshistorie für eine transparente Bedarfsermittlung.'}
                    onChange={(e) => setSettings({
                      ...settings,
                      homepage_link_text: { ...settings.homepage_link_text, hero_description: e.target.value }
                    })}
                    rows={3}
                    placeholder="Beschreibungstext auf der Startseite..."
                    data-testid="homepage-hero-description"
                  />
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label htmlFor="link-title" className="text-sm font-medium">Titel des Link-Hinweises</Label>
                  <p className="text-xs text-muted-foreground">Überschrift beim Link-Hinweisbereich auf der Ergebnisseite</p>
                  <Input
                    id="link-title"
                    value={settings.homepage_link_text?.title || 'Analyse-Link speichern'}
                    onChange={(e) => setSettings({
                      ...settings,
                      homepage_link_text: { ...settings.homepage_link_text, title: e.target.value }
                    })}
                    placeholder="z.B. Analyse-Link speichern"
                    data-testid="homepage-link-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link-description" className="text-sm font-medium">Beschreibung des Link-Hinweises</Label>
                  <p className="text-xs text-muted-foreground">Erläuterungstext beim Link-Hinweisbereich auf der Ergebnisseite</p>
                  <Textarea
                    id="link-description"
                    value={settings.homepage_link_text?.description || 'Speichern Sie den folgenden Link, um diese Analyse jederzeit erneut aufzurufen und zu bearbeiten. Eine Registrierung ist nicht erforderlich.'}
                    onChange={(e) => setSettings({
                      ...settings,
                      homepage_link_text: { ...settings.homepage_link_text, description: e.target.value }
                    })}
                    rows={3}
                    placeholder="Beschreibungstext..."
                    data-testid="homepage-link-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="footer-hint" className="text-sm font-medium">Hinweis auf der Startseite</Label>
                  <p className="text-xs text-muted-foreground">Kurzer Hinweis unter dem "Analyse starten" Button auf der Startseite</p>
                  <Input
                    id="footer-hint"
                    value={settings.homepage_link_text?.footer_hint || 'Am Ende wird Ihnen ein permanenter Link angezeigt – speichern Sie ihn, um Ihre Analyse später aufzurufen.'}
                    onChange={(e) => setSettings({
                      ...settings,
                      homepage_link_text: { ...settings.homepage_link_text, footer_hint: e.target.value }
                    })}
                    placeholder="Hinweistext..."
                    data-testid="homepage-footer-hint"
                  />
                </div>
              </div>

              <Separator />

              <div className="p-4 bg-secondary/50 rounded-lg">
                <h4 className="font-semibold text-sm mb-3">Vorschau - Ergebnisseite</h4>
                <div className="p-4 border bg-white rounded-lg">
                  <p className="font-bold text-base mb-1">{settings.homepage_link_text?.title || 'Analyse-Link speichern'}</p>
                  <p className="text-sm text-muted-foreground">{settings.homepage_link_text?.description || 'Speichern Sie den folgenden Link, um diese Analyse jederzeit erneut aufzurufen und zu bearbeiten. Eine Registrierung ist nicht erforderlich.'}</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-semibold text-sm">SEO-Bereich (Suchmaschinenoptimierung)</h4>
                <p className="text-xs text-muted-foreground">Dieser Text erscheint am unteren Ende der Startseite und hilft bei der Suchmaschinenoptimierung.</p>
                <div className="space-y-2">
                  <Label htmlFor="seo-heading" className="text-sm font-medium">SEO-Überschrift (H2)</Label>
                  <Input
                    id="seo-heading"
                    value={settings.seo_section?.heading || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo_section: { ...settings.seo_section, heading: e.target.value }
                    })}
                    placeholder="SEO-Überschrift mit Keyword..."
                    data-testid="seo-heading"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seo-content" className="text-sm font-medium">SEO-Text</Label>
                  <Textarea
                    id="seo-content"
                    value={settings.seo_section?.content || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo_section: { ...settings.seo_section, content: e.target.value }
                    })}
                    rows={6}
                    placeholder="Beschreibungstext für Suchmaschinen..."
                    data-testid="seo-content"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seo-backlink-bullets" className="text-sm font-medium">Backlink-Banner Stichpunkte</Label>
                  <p className="text-xs text-muted-foreground">Bis zu 3 Stichpunkte, getrennt durch | (z.B. "Kostenlose Analyse|Transparente Berechnung|Für Verwalter")</p>
                  <Input
                    id="seo-backlink-bullets"
                    value={settings.seo_section?.backlink_bullets || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo_section: { ...settings.seo_section, backlink_bullets: e.target.value }
                    })}
                    placeholder="Stichpunkt 1|Stichpunkt 2|Stichpunkt 3"
                    data-testid="seo-backlink-bullets"
                  />
                </div>
              </div>

              <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-texts-btn">
                <Save className="h-4 w-4" /> Einstellungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meta">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Meta-Daten & SEO</CardTitle>
              <CardDescription>
                Konfigurieren Sie die Meta-Tags für Suchmaschinen und Social Media. Diese Daten werden im HTML-Head der Webseite ausgegeben.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-sm">Allgemeine Meta-Daten</h4>
                <div className="space-y-2">
                  <Label htmlFor="meta-site-title" className="text-sm font-medium">Seitentitel (title)</Label>
                  <p className="text-xs text-muted-foreground">Wird im Browser-Tab und in Suchergebnissen angezeigt</p>
                  <Input
                    id="meta-site-title"
                    value={settings.meta_tags?.site_title || ''}
                    onChange={(e) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, site_title: e.target.value } })}
                    placeholder="WEG-Analysetool | Verwaltungsbedarf berechnen"
                    data-testid="meta-site-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-description" className="text-sm font-medium">Meta-Beschreibung (description)</Label>
                  <p className="text-xs text-muted-foreground">Kurze Beschreibung der Seite für Suchergebnisse (empfohlen: 150–160 Zeichen)</p>
                  <Textarea
                    id="meta-description"
                    value={settings.meta_tags?.meta_description || ''}
                    onChange={(e) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, meta_description: e.target.value } })}
                    rows={3}
                    placeholder="Ermitteln Sie den Verwaltungsbedarf Ihrer WEG..."
                    data-testid="meta-description"
                  />
                  <p className="text-xs text-muted-foreground text-right">{(settings.meta_tags?.meta_description || '').length} Zeichen</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-robots" className="text-sm font-medium">Robots</Label>
                  <p className="text-xs text-muted-foreground">Steuert, ob Suchmaschinen die Seite indexieren sollen</p>
                  <Select
                    value={settings.meta_tags?.robots || 'index, follow'}
                    onValueChange={(v) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, robots: v } })}
                  >
                    <SelectTrigger id="meta-robots" data-testid="meta-robots">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="index, follow">index, follow (Standard)</SelectItem>
                      <SelectItem value="noindex, follow">noindex, follow</SelectItem>
                      <SelectItem value="index, nofollow">index, nofollow</SelectItem>
                      <SelectItem value="noindex, nofollow">noindex, nofollow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-canonical" className="text-sm font-medium">Canonical URL</Label>
                  <p className="text-xs text-muted-foreground">Bevorzugte URL für diese Seite (optional, für SEO)</p>
                  <Input
                    id="meta-canonical"
                    value={settings.meta_tags?.canonical_url || ''}
                    onChange={(e) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, canonical_url: e.target.value } })}
                    placeholder="https://verwalterhonorar.de"
                    data-testid="meta-canonical"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-semibold text-sm">Open Graph (Social Media)</h4>
                <p className="text-xs text-muted-foreground">Diese Daten werden angezeigt, wenn die Seite auf Facebook, LinkedIn, WhatsApp etc. geteilt wird.</p>
                <div className="space-y-2">
                  <Label htmlFor="og-title" className="text-sm font-medium">OG Titel</Label>
                  <Input
                    id="og-title"
                    value={settings.meta_tags?.og_title || ''}
                    onChange={(e) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, og_title: e.target.value } })}
                    placeholder="WEG-Analysetool – Verwaltungsbedarf transparent ermitteln"
                    data-testid="meta-og-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="og-description" className="text-sm font-medium">OG Beschreibung</Label>
                  <Textarea
                    id="og-description"
                    value={settings.meta_tags?.og_description || ''}
                    onChange={(e) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, og_description: e.target.value } })}
                    rows={2}
                    placeholder="Kostenloser Gebührenrechner für WEG..."
                    data-testid="meta-og-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="og-image" className="text-sm font-medium">OG Bild-URL</Label>
                  <p className="text-xs text-muted-foreground">URL zu einem Vorschaubild (empfohlen: 1200x630px)</p>
                  <Input
                    id="og-image"
                    value={settings.meta_tags?.og_image || ''}
                    onChange={(e) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, og_image: e.target.value } })}
                    placeholder="https://example.com/og-image.jpg"
                    data-testid="meta-og-image"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="og-type" className="text-sm font-medium">OG Typ</Label>
                  <Select
                    value={settings.meta_tags?.og_type || 'website'}
                    onValueChange={(v) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, og_type: v } })}
                  >
                    <SelectTrigger id="og-type" data-testid="meta-og-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="website">website</SelectItem>
                      <SelectItem value="article">article</SelectItem>
                      <SelectItem value="product">product</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-semibold text-sm">Twitter Card</h4>
                <div className="space-y-2">
                  <Label htmlFor="twitter-card" className="text-sm font-medium">Card-Typ</Label>
                  <Select
                    value={settings.meta_tags?.twitter_card || 'summary'}
                    onValueChange={(v) => setSettings({ ...settings, meta_tags: { ...settings.meta_tags, twitter_card: v } })}
                  >
                    <SelectTrigger id="twitter-card" data-testid="meta-twitter-card">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="summary">summary</SelectItem>
                      <SelectItem value="summary_large_image">summary_large_image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="p-4 bg-secondary/50 rounded-lg">
                <h4 className="font-semibold text-sm mb-3">Vorschau - Suchergebnis</h4>
                <div className="p-4 border bg-white rounded-lg space-y-1 max-w-lg">
                  <p className="text-blue-700 text-base font-medium truncate">{settings.meta_tags?.site_title || 'WEG-Analysetool | Verwaltungsbedarf'}</p>
                  <p className="text-emerald-800 text-xs truncate">{settings.meta_tags?.canonical_url || 'https://verwalterhonorar.de'}</p>
                  <p className="text-sm text-muted-foreground line-clamp-2">{settings.meta_tags?.meta_description || 'Keine Beschreibung hinterlegt'}</p>
                </div>
              </div>

              <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-meta-btn">
                <Save className="h-4 w-4" /> Meta-Daten speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Verwaltungshistorie - Bewertungskriterien</CardTitle>
                  <CardDescription>Verwalten Sie die Kriterien, die bei der Bewertung der Verwaltungshistorie berücksichtigt werden, sowie deren Gewichtung.</CardDescription>
                </div>
                <Button onClick={openAddHistoryDialog} className="gap-2" data-testid="add-history-btn">
                  <Plus className="h-4 w-4" /> Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="history-items-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium">Bezeichnung</th>
                      <th className="text-left py-2 px-2 font-medium">Typ</th>
                      <th className="text-left py-2 px-2 font-medium">Gewichtungen</th>
                      <th className="text-center py-2 px-2 font-medium">Aktiv</th>
                      <th className="text-right py-2 px-2 font-medium">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getHistoryItems().map((item, index, arr) => (
                      <tr key={index} className={`border-b hover:bg-muted/50 ${!item.active ? 'opacity-50' : ''}`}>
                        <td className="py-2 px-2">{item.label}</td>
                        <td className="py-2 px-2 text-muted-foreground text-xs">
                          {HISTORY_TYPE_OPTIONS.find(o => o.value === item.type)?.label || item.type}
                        </td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">
                          {item.type === 'freq' && `Keine: ${item.weight_none}, Selten: ${item.weight_selten}, Häufig: ${item.weight_haeufig}`}
                          {(item.type === 'level' || item.type === 'level_inv') && `Niedrig: ${item.weight_niedrig}, Mittel: ${item.weight_mittel}, Hoch: ${item.weight_hoch}`}
                          {item.type === 'sepa' && `Alle: ${item.weight_alle}, Meiste: ${item.weight_meiste}, Wenige: ${item.weight_wenige}`}
                          {item.type === 'bool_inv' && `Ja: ${item.weight_true}, Nein: ${item.weight_false}`}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Switch checked={item.active !== false} onCheckedChange={() => toggleHistoryActive(index)} data-testid={`history-active-${index}`} />
                        </td>
                        <td className="py-2 px-2 text-right">
                          <div className="flex justify-end gap-1">
                            <button disabled={index === 0} onClick={() => moveItem('history_items', index, -1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronUp className="h-3 w-3" /></button>
                            <button disabled={index === arr.length - 1} onClick={() => moveItem('history_items', index, 1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronDown className="h-3 w-3" /></button>
                            <Button variant="ghost" size="sm" onClick={() => openEditHistoryDialog(index)} data-testid={`edit-history-${index}`}><Pencil className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteHistoryItem(index)} data-testid={`delete-history-${index}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {getHistoryItems().length === 0 && (
                      <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Keine Historie-Punkte vorhanden. Klicken Sie auf "Hinzufügen" um einen zu erstellen.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Button onClick={saveSettings} disabled={saving} className="w-full mt-6 gap-2" data-testid="save-history-btn">
                <Save className="h-4 w-4" /> Alle Änderungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="modernizations">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Geplante oder anstehende Modernisierungen</CardTitle>
                  <CardDescription>Verwalten Sie die Liste der Modernisierungsposten, die im Analyse-Wizard zur Auswahl stehen.</CardDescription>
                </div>
                <Button onClick={openAddModernizationDialog} className="gap-2" data-testid="add-modernization-btn">
                  <Plus className="h-4 w-4" /> Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Status</TableHead>
                      <TableHead>Modernisierungsposten</TableHead>
                      <TableHead className="w-36">Bedingung</TableHead>
                      <TableHead className="w-28 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(!settings?.modernization_items || settings.modernization_items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          Keine Modernisierungsposten definiert. Klicken Sie auf "Hinzufügen" um einen neuen Posten anzulegen.
                        </TableCell>
                      </TableRow>
                    )}
                    {settings?.modernization_items?.map((item, i, arr) => (
                      <TableRow key={i} className={item.active === false ? 'opacity-50' : ''}>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleModernizationActive(i)}
                            className="h-8 w-8 p-0"
                            data-testid={`modernization-toggle-${i}`}
                          >
                            {item.active !== false ? (
                              <ToggleRight className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{item.label}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {item.required_equipment
                            ? EQUIPMENT_OPTIONS.find(e => e.value === item.required_equipment)?.label || item.required_equipment
                            : 'Immer'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button disabled={i === 0} onClick={() => moveItem('modernization_items', i, -1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronUp className="h-3.5 w-3.5" /></button>
                            <button disabled={i === arr.length - 1} onClick={() => moveItem('modernization_items', i, 1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronDown className="h-3.5 w-3.5" /></button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModernizationDialog(i)}
                              className="h-8 w-8 p-0"
                              data-testid={`modernization-edit-${i}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteModernizationItem(i)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`modernization-delete-${i}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Diese Posten erscheinen im Wizard unter "Umfangreiche Modernisierungen" (für durchgeführte) und "Geplante oder anstehende Modernisierungen" (für geplante Maßnahmen).
              </p>
              <Button onClick={saveSettings} disabled={saving} className="gap-2 mt-4" data-testid="save-modernizations-btn">
                <Save className="h-4 w-4" /> Alle Änderungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="repairs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Größere Reparaturen</CardTitle>
                  <CardDescription>Verwalten Sie die Liste der Reparaturtypen und deren Negativwertung. Größere Reparaturen weisen auf mögliche weitere Mängel hin.</CardDescription>
                </div>
                <Button onClick={openAddRepairDialog} className="gap-2" data-testid="add-repair-btn">
                  <Plus className="h-4 w-4" /> Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Status</TableHead>
                      <TableHead>Reparaturtyp</TableHead>
                      <TableHead className="w-32">Negativwertung</TableHead>
                      <TableHead className="w-28 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(!settings.repair_items || settings.repair_items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          Keine Reparaturtypen definiert. Klicken Sie auf "Hinzufügen" um einen neuen Typ anzulegen.
                        </TableCell>
                      </TableRow>
                    )}
                    {settings.repair_items?.map((item, i, arr) => (
                      <TableRow key={i} className={item.active === false ? 'opacity-50' : ''}>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRepairActive(i)}
                            className="h-8 w-8 p-0"
                            data-testid={`repair-toggle-${i}`}
                          >
                            {item.active !== false ? (
                              <ToggleRight className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{item.label}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="20"
                              value={item.weight}
                              onChange={e => updateRepairWeight(i, e.target.value)}
                              className="h-8 text-sm w-16"
                              data-testid={`repair-weight-${i}`}
                            />
                            <span className="text-xs text-muted-foreground">Punkte</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button disabled={i === 0} onClick={() => moveItem('repair_items', i, -1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronUp className="h-3.5 w-3.5" /></button>
                            <button disabled={i === arr.length - 1} onClick={() => moveItem('repair_items', i, 1)} className="p-1 hover:bg-secondary rounded disabled:opacity-20"><ChevronDown className="h-3.5 w-3.5" /></button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditRepairDialog(i)}
                              className="h-8 w-8 p-0"
                              data-testid={`repair-edit-${i}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteRepairItem(i)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`repair-delete-${i}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Die Negativwertung gibt an, wie viele Punkte zum Gebäudezustand-Score hinzugefügt werden, wenn eine Reparatur ausgewählt wird. Höhere Werte = stärkerer negativer Einfluss.
              </p>
              <Button onClick={saveSettings} disabled={saving} className="gap-2 mt-4" data-testid="save-repairs-btn">
                <Save className="h-4 w-4" /> Alle Änderungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="costs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Kostenschätzung Sanierungsbedarf</CardTitle>
                  <CardDescription>Konfigurieren Sie die geschätzten Kosten pro Sanierungskomponente. Diese werden bei fälligem Sanierungsbedarf automatisch berechnet.</CardDescription>
                </div>
                <Button onClick={addCostEstimate} className="gap-2" data-testid="add-cost-btn">
                  <Plus className="h-4 w-4" /> Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {getCostEstimates().map((item, i) => (
                <div key={i} className={`p-4 border space-y-3 ${item.active === false ? 'opacity-50' : ''}`} data-testid={`cost-item-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button variant="ghost" size="sm" onClick={() => updateCostEstimate(i, 'active', !item.active)} className="h-8 w-8 p-0">
                        {item.active !== false ? <ToggleRight className="h-5 w-5 text-emerald-600" /> : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                      </Button>
                      <Input value={item.label} onChange={e => updateCostEstimate(i, 'label', e.target.value)} placeholder="Bezeichnung" className="h-8 text-sm w-48" data-testid={`cost-label-${i}`} />
                      <Input value={item.key} onChange={e => updateCostEstimate(i, 'key', e.target.value)} placeholder="Schlüssel" className="h-8 text-sm w-32 text-muted-foreground" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={item.type} onValueChange={v => updateCostEstimate(i, 'type', v)}>
                        <SelectTrigger className="h-8 w-36 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="per_unit">Pro Wohnung</SelectItem>
                          <SelectItem value="bracket">Staffel</SelectItem>
                          <SelectItem value="tiered">Gestaffelt + Zuschlag</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => deleteCostEstimate(i)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  {item.type === 'per_unit' && (
                    <div className="flex items-center gap-2 pl-11">
                      <span className="text-sm text-muted-foreground">Kosten pro Wohnung:</span>
                      <Input type="number" value={item.cost_per_unit || 0} onChange={e => updateCostEstimate(i, 'cost_per_unit', parseInt(e.target.value) || 0)} className="h-8 text-sm w-24" data-testid={`cost-perunit-${i}`} />
                      <span className="text-sm text-muted-foreground">€</span>
                    </div>
                  )}

                  {item.type === 'bracket' && (
                    <div className="pl-11 space-y-2">
                      <span className="text-xs text-muted-foreground uppercase font-semibold">Staffeln (bis X Wohnungen → Kosten)</span>
                      {(item.brackets || []).map((b, bi) => (
                        <div key={bi} className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground w-8">bis</span>
                          <Input type="number" value={b.max_units} onChange={e => updateCostBracket(i, bi, 'max_units', e.target.value)} className="h-8 text-sm w-20" />
                          <span className="text-sm text-muted-foreground">WE →</span>
                          <Input type="number" value={b.cost} onChange={e => updateCostBracket(i, bi, 'cost', e.target.value)} className="h-8 text-sm w-24" />
                          <span className="text-sm text-muted-foreground">€</span>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => {
                        const items = [...getCostEstimates()];
                        const brackets = [...(items[i].brackets || [])];
                        brackets.push({ max_units: 9999, cost: 0 });
                        items[i] = { ...items[i], brackets };
                        setSettings({ ...settings, refurbishment_cost_estimates: items });
                      }} className="text-xs h-7">+ Staffel hinzufügen</Button>
                    </div>
                  )}

                  {item.type === 'tiered' && (
                    <div className="pl-11 space-y-2">
                      <span className="text-xs text-muted-foreground uppercase font-semibold">Gestaffelt mit Zuschlag pro Extra-Wohnung</span>
                      {(item.tiers || []).map((t, ti) => (
                        <div key={ti} className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-muted-foreground w-8">bis</span>
                          <Input type="number" value={t.max_units} onChange={e => updateCostTier(i, ti, 'max_units', e.target.value)} className="h-8 text-sm w-20" />
                          <span className="text-sm text-muted-foreground">WE: Basis</span>
                          <Input type="number" value={t.base_cost} onChange={e => updateCostTier(i, ti, 'base_cost', e.target.value)} className="h-8 text-sm w-24" />
                          <span className="text-sm text-muted-foreground">€ + je</span>
                          <Input type="number" value={t.per_extra_unit || 0} onChange={e => updateCostTier(i, ti, 'per_extra_unit', e.target.value)} className="h-8 text-sm w-20" />
                          <span className="text-sm text-muted-foreground">€ ab WE</span>
                          <Input type="number" value={t.extra_from || 0} onChange={e => updateCostTier(i, ti, 'extra_from', e.target.value)} className="h-8 text-sm w-16" />
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => {
                        const items = [...getCostEstimates()];
                        const tiers = [...(items[i].tiers || [])];
                        tiers.push({ max_units: 9999, base_cost: 0, per_extra_unit: 0, extra_from: 0 });
                        items[i] = { ...items[i], tiers };
                        setSettings({ ...settings, refurbishment_cost_estimates: items });
                      }} className="text-xs h-7">+ Stufe hinzufügen</Button>
                    </div>
                  )}
                </div>
              ))}
              <Button onClick={saveSettings} disabled={saving} className="gap-2 mt-4" data-testid="save-costs-btn">
                <Save className="h-4 w-4" /> Alle Änderungen speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="thresholds">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Schwellenwerte für Sanierungsbedarf</CardTitle>
                  <CardDescription>
                    Verwalten Sie die Komponenten und deren Lebensdauer-Schwellenwerte für die Berechnung des "Kommenden Sanierungsbedarfs".
                  </CardDescription>
                </div>
                <Button onClick={openAddThresholdDialog} className="gap-2" data-testid="add-threshold-btn">
                  <Plus className="h-4 w-4" /> Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Status</TableHead>
                      <TableHead>Komponente</TableHead>
                      <TableHead className="w-32">Schwellenwert</TableHead>
                      <TableHead className="w-36">Bedingung</TableHead>
                      <TableHead className="w-28 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getThresholdsArray().map((item, i) => (
                      <TableRow key={i} className={item.active === false ? 'opacity-50' : ''}>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleThresholdActive(i)}
                            className="h-8 w-8 p-0"
                            data-testid={`threshold-toggle-${i}`}
                          >
                            {item.active !== false ? (
                              <ToggleRight className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{item.label}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="150"
                              value={item.years}
                              onChange={e => updateThresholdYears(i, e.target.value)}
                              className="h-8 text-sm w-16"
                              data-testid={`threshold-years-${i}`}
                            />
                            <span className="text-xs text-muted-foreground">Jahre</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {item.required_equipment
                            ? EQUIPMENT_OPTIONS.find(e => e.value === item.required_equipment)?.label || item.required_equipment
                            : 'Immer'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditThresholdDialog(i)}
                              className="h-8 w-8 p-0"
                              data-testid={`threshold-edit-${i}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteThresholdItem(i)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`threshold-delete-${i}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Separator className="my-4" />
              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={resetThresholds} data-testid="reset-thresholds-btn">
                  Auf Standard zurücksetzen
                </Button>
                <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-thresholds-btn">
                  <Save className="h-4 w-4" /> {saving ? 'Speichern...' : 'Schwellenwerte speichern'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visibility">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Öffentliche Sichtbarkeit</CardTitle>
              <CardDescription>Steuern Sie, welche Informationen für Kunden bei der öffentlichen Analyse sichtbar sind.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'show_demand_category', label: 'Bedarfskategorie anzeigen', desc: 'Kunden sehen ob der Bedarf unter-/durchschnittlich ist' },
                { key: 'show_scores', label: 'Detaillierte Scores anzeigen', desc: 'Komplexität, Zustand und Historie-Scores' },
                { key: 'show_fees', label: 'Vergütungskalkulation anzeigen', desc: 'Geschätzte Gebühren für den Kunden sichtbar' },
                { key: 'show_building_details', label: 'Gebäudedetails anzeigen', desc: 'Baujahr, Renovierungen, Sanierungsbedarf etc.' },
                { key: 'show_recommendation', label: 'Vergütungsempfehlung anzeigen', desc: 'Empfehlung für Grundvergütung basierend auf Analyse' },
                { key: 'show_pdf_download', label: 'PDF-Download erlauben', desc: 'Kunden können das Analyse-Ergebnis als PDF herunterladen' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-3 rounded-md border">
                  <div>
                    <Label className="text-sm font-medium">{item.label}</Label>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <Switch checked={settings.public_visibility?.[item.key] ?? false} onCheckedChange={() => toggleVisibility(item.key)} data-testid={`visibility-${item.key}`} />
                </div>
              ))}
              <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-visibility-btn">
                <Save className="h-4 w-4" /> Sichtbarkeit speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="privacy">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Datenschutzerklärung</CardTitle>
              <CardDescription>Inhalt der öffentlichen Datenschutzseite. Sie können HTML oder einfachen Text verwenden.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea value={privacy} onChange={e => setPrivacy(e.target.value)} rows={20} placeholder="Geben Sie hier Ihre Datenschutzerklärung ein..." className="font-mono text-sm" data-testid="privacy-textarea" />
              <Button onClick={savePrivacy} disabled={saving} className="gap-2" data-testid="save-privacy-btn">
                <Save className="h-4 w-4" /> Datenschutz speichern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Service Add/Edit Dialog */}
      <Dialog open={serviceDialog} onOpenChange={setServiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingServiceIndex !== null ? 'Leistung bearbeiten' : 'Neue Leistung hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="service-name">Bezeichnung</Label>
              <Input
                id="service-name"
                value={serviceForm.name}
                onChange={e => setServiceForm({ ...serviceForm, name: e.target.value })}
                placeholder="z.B. Eigentumswechsel bearbeiten"
                data-testid="service-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service-price">Preis (EUR)</Label>
                <Input
                  id="service-price"
                  type="number"
                  step="0.01"
                  value={serviceForm.price}
                  onChange={e => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) || 0 })}
                  data-testid="service-form-price"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-unit">Einheit</Label>
                <Select
                  value={serviceForm.unit}
                  onValueChange={v => setServiceForm({ ...serviceForm, unit: v })}
                >
                  <SelectTrigger data-testid="service-form-unit">
                    <SelectValue placeholder="Einheit wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="service-active"
                checked={serviceForm.active !== false}
                onCheckedChange={v => setServiceForm({ ...serviceForm, active: v })}
              />
              <Label htmlFor="service-active">Leistung aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setServiceDialog(false)}>Abbrechen</Button>
            <Button onClick={saveService} disabled={!serviceForm.name} data-testid="service-form-save">
              {editingServiceIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair Item Add/Edit Dialog */}
      <Dialog open={repairDialog} onOpenChange={setRepairDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRepairIndex !== null ? 'Reparaturtyp bearbeiten' : 'Neuen Reparaturtyp hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="repair-label">Bezeichnung</Label>
              <Input
                id="repair-label"
                value={repairForm.label}
                onChange={e => setRepairForm({ ...repairForm, label: e.target.value })}
                placeholder="z.B. Heizungsreparatur"
                data-testid="repair-form-label"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="repair-weight">Negativwertung (Punkte)</Label>
              <p className="text-xs text-muted-foreground">Wie stark soll diese Reparatur den Gebäudezustand-Score negativ beeinflussen?</p>
              <Input
                id="repair-weight"
                type="number"
                min="0"
                max="20"
                value={repairForm.weight}
                onChange={e => setRepairForm({ ...repairForm, weight: parseInt(e.target.value) || 0 })}
                data-testid="repair-form-weight"
              />
            </div>
            <div className="space-y-2">
              <Label>Bedingung (optional)</Label>
              <Select value={repairForm.required_equipment || 'none'} onValueChange={v => setRepairForm({ ...repairForm, required_equipment: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="repair-form-equipment">
                  <SelectValue placeholder="Bedingung wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Immer anzeigen</SelectItem>
                  <SelectItem value="has_elevator">Nur mit Aufzug</SelectItem>
                  <SelectItem value="has_central_heating">Nur mit Zentralheizung</SelectItem>
                  <SelectItem value="has_underground_garage">Nur mit Tiefgarage</SelectItem>
                  <SelectItem value="has_double_parkers">Nur mit Doppelparkern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="repair-active"
                checked={repairForm.active !== false}
                onCheckedChange={v => setRepairForm({ ...repairForm, active: v })}
              />
              <Label htmlFor="repair-active">Reparaturtyp aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairDialog(false)}>Abbrechen</Button>
            <Button onClick={saveRepairItem} disabled={!repairForm.label} data-testid="repair-form-save">
              {editingRepairIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Threshold Add/Edit Dialog */}
      <Dialog open={thresholdDialog} onOpenChange={setThresholdDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingThresholdIndex !== null ? 'Schwellenwert bearbeiten' : 'Neuen Schwellenwert hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="threshold-label">Komponente</Label>
              <Input
                id="threshold-label"
                value={thresholdForm.label}
                onChange={e => setThresholdForm({ ...thresholdForm, label: e.target.value })}
                placeholder="z.B. Dachsanierung"
                data-testid="threshold-form-label"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="threshold-years">Schwellenwert (Jahre)</Label>
              <p className="text-xs text-muted-foreground">Nach wie vielen Jahren gilt die Komponente als sanierungsbedürftig?</p>
              <Input
                id="threshold-years"
                type="number"
                min="1"
                max="150"
                value={thresholdForm.years}
                onChange={e => setThresholdForm({ ...thresholdForm, years: parseInt(e.target.value) || 0 })}
                data-testid="threshold-form-years"
              />
            </div>
            <div className="space-y-2">
              <Label>Bedingung</Label>
              <p className="text-xs text-muted-foreground">Nur anzeigen, wenn bestimmte Ausstattung vorhanden ist?</p>
              <Select value={thresholdForm.required_equipment || 'none'} onValueChange={v => setThresholdForm({ ...thresholdForm, required_equipment: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="threshold-form-equipment">
                  <SelectValue placeholder="Bedingung wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Immer anzeigen</SelectItem>
                  <SelectItem value="has_elevator">Nur mit Aufzug</SelectItem>
                  <SelectItem value="has_central_heating">Nur mit Zentralheizung</SelectItem>
                  <SelectItem value="has_underground_garage">Nur mit Tiefgarage</SelectItem>
                  <SelectItem value="has_double_parkers">Nur mit Doppelparkern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="threshold-active"
                checked={thresholdForm.active !== false}
                onCheckedChange={v => setThresholdForm({ ...thresholdForm, active: v })}
              />
              <Label htmlFor="threshold-active">Schwellenwert aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setThresholdDialog(false)}>Abbrechen</Button>
            <Button onClick={saveThresholdItem} disabled={!thresholdForm.label} data-testid="threshold-form-save">
              {editingThresholdIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modernization Item Add/Edit Dialog */}
      <Dialog open={modernizationDialog} onOpenChange={setModernizationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingModernizationIndex !== null ? 'Modernisierungsposten bearbeiten' : 'Neuen Modernisierungsposten hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="modernization-label">Bezeichnung</Label>
              <Input
                id="modernization-label"
                value={modernizationForm.label}
                onChange={e => setModernizationForm({ ...modernizationForm, label: e.target.value })}
                placeholder="z.B. Erneuerung der Heizungsanlage"
                data-testid="modernization-form-label"
              />
            </div>
            <div className="space-y-2">
              <Label>Bedingung (optional)</Label>
              <p className="text-xs text-muted-foreground">Nur anzeigen, wenn bestimmte Ausstattung vorhanden ist?</p>
              <Select value={modernizationForm.required_equipment || 'none'} onValueChange={v => setModernizationForm({ ...modernizationForm, required_equipment: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="modernization-form-equipment">
                  <SelectValue placeholder="Bedingung wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Immer anzeigen</SelectItem>
                  <SelectItem value="has_elevator">Nur mit Aufzug</SelectItem>
                  <SelectItem value="has_central_heating">Nur mit Zentralheizung</SelectItem>
                  <SelectItem value="has_underground_garage">Nur mit Tiefgarage</SelectItem>
                  <SelectItem value="has_double_parkers">Nur mit Doppelparkern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="modernization-active"
                checked={modernizationForm.active !== false}
                onCheckedChange={v => setModernizationForm({ ...modernizationForm, active: v })}
              />
              <Label htmlFor="modernization-active">Posten aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModernizationDialog(false)}>Abbrechen</Button>
            <Button onClick={saveModernizationItem} disabled={!modernizationForm.label} data-testid="modernization-form-save">
              {editingModernizationIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Item Add/Edit Dialog */}
      <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingHistoryIndex !== null ? 'Historie-Punkt bearbeiten' : 'Neuen Historie-Punkt hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="history-label">Bezeichnung</Label>
              <Input
                id="history-label"
                value={historyForm.label}
                onChange={e => setHistoryForm({ ...historyForm, label: e.target.value })}
                placeholder="z.B. Außerordentliche Eigentümerversammlungen"
                data-testid="history-form-label"
              />
            </div>
            <div className="space-y-2">
              <Label>Bewertungstyp</Label>
              <Select value={historyForm.type} onValueChange={v => setHistoryForm({ ...historyForm, type: v })}>
                <SelectTrigger data-testid="history-form-type">
                  <SelectValue placeholder="Typ wählen" />
                </SelectTrigger>
                <SelectContent>
                  {HISTORY_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Weight inputs based on type */}
            {historyForm.type === 'freq' && (
              <div className="space-y-3 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-medium text-muted-foreground">Gewichtung (höher = negativer Einfluss)</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Keine</Label>
                    <Input type="number" value={historyForm.weight_none || 0} onChange={e => setHistoryForm({ ...historyForm, weight_none: parseInt(e.target.value) || 0 })} data-testid="history-weight-none" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Selten</Label>
                    <Input type="number" value={historyForm.weight_selten || 0} onChange={e => setHistoryForm({ ...historyForm, weight_selten: parseInt(e.target.value) || 0 })} data-testid="history-weight-selten" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Häufig</Label>
                    <Input type="number" value={historyForm.weight_haeufig || 0} onChange={e => setHistoryForm({ ...historyForm, weight_haeufig: parseInt(e.target.value) || 0 })} data-testid="history-weight-haeufig" />
                  </div>
                </div>
              </div>
            )}
            
            {(historyForm.type === 'level' || historyForm.type === 'level_inv') && (
              <div className="space-y-3 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-medium text-muted-foreground">
                  Gewichtung {historyForm.type === 'level_inv' ? '(Hoch = positiv)' : '(Hoch = negativ)'}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Niedrig</Label>
                    <Input type="number" value={historyForm.weight_niedrig || 0} onChange={e => setHistoryForm({ ...historyForm, weight_niedrig: parseInt(e.target.value) || 0 })} data-testid="history-weight-niedrig" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Mittel</Label>
                    <Input type="number" value={historyForm.weight_mittel || 0} onChange={e => setHistoryForm({ ...historyForm, weight_mittel: parseInt(e.target.value) || 0 })} data-testid="history-weight-mittel" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Hoch</Label>
                    <Input type="number" value={historyForm.weight_hoch || 0} onChange={e => setHistoryForm({ ...historyForm, weight_hoch: parseInt(e.target.value) || 0 })} data-testid="history-weight-hoch" />
                  </div>
                </div>
              </div>
            )}
            
            {historyForm.type === 'sepa' && (
              <div className="space-y-3 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-medium text-muted-foreground">Gewichtung (höher = negativer Einfluss)</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Alle</Label>
                    <Input type="number" value={historyForm.weight_alle || 0} onChange={e => setHistoryForm({ ...historyForm, weight_alle: parseInt(e.target.value) || 0 })} data-testid="history-weight-alle" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Meiste</Label>
                    <Input type="number" value={historyForm.weight_meiste || 0} onChange={e => setHistoryForm({ ...historyForm, weight_meiste: parseInt(e.target.value) || 0 })} data-testid="history-weight-meiste" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Wenige</Label>
                    <Input type="number" value={historyForm.weight_wenige || 0} onChange={e => setHistoryForm({ ...historyForm, weight_wenige: parseInt(e.target.value) || 0 })} data-testid="history-weight-wenige" />
                  </div>
                </div>
              </div>
            )}
            
            {historyForm.type === 'bool_inv' && (
              <div className="space-y-3 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-medium text-muted-foreground">Gewichtung (Ja = positiv)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Ja</Label>
                    <Input type="number" value={historyForm.weight_true || 0} onChange={e => setHistoryForm({ ...historyForm, weight_true: parseInt(e.target.value) || 0 })} data-testid="history-weight-true" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nein</Label>
                    <Input type="number" value={historyForm.weight_false || 0} onChange={e => setHistoryForm({ ...historyForm, weight_false: parseInt(e.target.value) || 0 })} data-testid="history-weight-false" />
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <Switch
                id="history-active"
                checked={historyForm.active !== false}
                onCheckedChange={v => setHistoryForm({ ...historyForm, active: v })}
              />
              <Label htmlFor="history-active">Kriterium aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialog(false)}>Abbrechen</Button>
            <Button onClick={saveHistoryItem} disabled={!historyForm.label} data-testid="history-form-save">
              {editingHistoryIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equipment Item Add/Edit Dialog */}
      <Dialog open={equipmentDialog} onOpenChange={setEquipmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEquipmentIndex !== null ? 'Ausstattungsmerkmal bearbeiten' : 'Neues Ausstattungsmerkmal hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bezeichnung *</Label>
              <Input value={equipmentForm.label} onChange={e => setEquipmentForm({ ...equipmentForm, label: e.target.value })} placeholder="z.B. Schwimmbad" data-testid="equipment-form-label" />
            </div>
            <div className="space-y-2">
              <Label>Schlüssel (intern)</Label>
              <Input value={equipmentForm.key} onChange={e => setEquipmentForm({ ...equipmentForm, key: e.target.value })} placeholder="Wird automatisch generiert" data-testid="equipment-form-key" />
              <p className="text-xs text-muted-foreground">Wird automatisch aus der Bezeichnung generiert, wenn leer gelassen.</p>
            </div>
            <div className="space-y-2">
              <Label>Komplexitätspunkte</Label>
              <Input type="number" value={equipmentForm.weight} onChange={e => setEquipmentForm({ ...equipmentForm, weight: parseInt(e.target.value) || 0 })} data-testid="equipment-form-weight" />
              <p className="text-xs text-muted-foreground">Positive Werte = höhere Komplexität, negative Werte = niedrigere Komplexität</p>
            </div>
            <div className="space-y-2">
              <Label>Beschreibung</Label>
              <Input value={equipmentForm.description || ''} onChange={e => setEquipmentForm({ ...equipmentForm, description: e.target.value })} placeholder="Kurze Erklärung" data-testid="equipment-form-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEquipmentDialog(false)}>Abbrechen</Button>
            <Button onClick={saveEquipmentItem} disabled={!equipmentForm.label} data-testid="equipment-form-save">
              {editingEquipmentIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Building Complexity Rule Add/Edit Dialog */}
      {/* Rating Label Dialog */}
      <Dialog open={ratingDialog} onOpenChange={setRatingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRatingIndex !== null ? 'Bewertungslabel bearbeiten' : 'Neues Bewertungslabel'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bezeichnung *</Label>
              <Input value={ratingForm.label} onChange={e => setRatingForm({ ...ratingForm, label: e.target.value })} placeholder="z.B. Durchschnittlich" data-testid="rating-form-label" />
            </div>
            <div>
              <Label>Schlüssel (Key)</Label>
              <Input value={ratingForm.key} onChange={e => setRatingForm({ ...ratingForm, key: e.target.value })} placeholder="Wird automatisch generiert" data-testid="rating-form-key" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min. Score</Label>
                <Input type="number" min="0" max="100" value={ratingForm.min_score} onChange={e => setRatingForm({ ...ratingForm, min_score: parseInt(e.target.value) || 0 })} data-testid="rating-form-min-score" />
              </div>
              <div>
                <Label>Max. Score</Label>
                <Input type="number" min="0" max="100" value={ratingForm.max_score} onChange={e => setRatingForm({ ...ratingForm, max_score: parseInt(e.target.value) || 0 })} data-testid="rating-form-max-score" />
              </div>
            </div>
            <div>
              <Label>Bedarfs-Prozent (%)</Label>
              <Input type="number" min="0" value={ratingForm.demand_pct} onChange={e => setRatingForm({ ...ratingForm, demand_pct: parseInt(e.target.value) || 100 })} data-testid="rating-form-demand-pct" />
              <p className="text-xs text-muted-foreground mt-1">100% = Basisgebühr, 150% = 50% Aufschlag, 50% = 50% Rabatt</p>
            </div>
            <div>
              <Label>Farbe</Label>
              <Select value={ratingForm.color || 'blue'} onValueChange={(v) => setRatingForm({ ...ratingForm, color: v })}>
                <SelectTrigger data-testid="rating-form-color">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${c.bg} ${c.border} border`}></span>
                        {c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveRatingItem} disabled={!ratingForm.label} data-testid="rating-form-save">
              {editingRatingIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={buildingDialog} onOpenChange={setBuildingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBuildingIndex !== null ? 'Gebäuderegel bearbeiten' : 'Neue Gebäuderegel'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bezeichnung *</Label>
              <Input value={buildingForm.label} onChange={e => setBuildingForm({ ...buildingForm, label: e.target.value })} placeholder="z.B. Dachgeschoss-Zuschlag" data-testid="building-form-label" />
            </div>
            <div>
              <Label>Schlüssel (Key)</Label>
              <Input value={buildingForm.key} onChange={e => setBuildingForm({ ...buildingForm, key: e.target.value })} placeholder="Wird automatisch generiert" data-testid="building-form-key" />
            </div>
            <div>
              <Label>Regeltyp</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                value={buildingForm.type}
                onChange={e => setBuildingForm({ ...buildingForm, type: e.target.value })}
                data-testid="building-form-type"
              >
                <option value="per_building">Pro Gebäude (Punkte × Anzahl, mit Max.)</option>
                <option value="threshold_below">Schwellenwert unter (Einh./Gebäude &lt; Schwelle)</option>
                <option value="threshold_above">Schwellenwert über (Einh./Gebäude &gt;= Schwelle)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Punkte</Label>
                <Input type="number" value={buildingForm.points} onChange={e => setBuildingForm({ ...buildingForm, points: parseFloat(e.target.value) || 0 })} data-testid="building-form-points" />
              </div>
              {buildingForm.type === 'per_building' ? (
                <div>
                  <Label>Max. Punkte</Label>
                  <Input type="number" value={buildingForm.max_points} onChange={e => setBuildingForm({ ...buildingForm, max_points: parseFloat(e.target.value) || 0 })} data-testid="building-form-max-points" />
                </div>
              ) : (
                <div>
                  <Label>Schwellenwert (Einh./Geb.)</Label>
                  <Input type="number" value={buildingForm.threshold} onChange={e => setBuildingForm({ ...buildingForm, threshold: parseFloat(e.target.value) || 0 })} data-testid="building-form-threshold" />
                </div>
              )}
            </div>
            <div>
              <Label>Beschreibung (optional)</Label>
              <Input value={buildingForm.description || ''} onChange={e => setBuildingForm({ ...buildingForm, description: e.target.value })} placeholder="Kurze Erklärung" data-testid="building-form-description" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveBuildingItem} disabled={!buildingForm.label} data-testid="building-form-save">
              {editingBuildingIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
